import { useEffect, useState, useCallback } from "react";
import { NavLink, Outlet, useNavigate, useSearchParams, useParams } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import {
  Brain, LayoutGrid, PawPrint, Users, MapPin, Building2, Scissors, Boxes,
  Sparkles, TrendingUp, AlertTriangle, RefreshCw, ArrowLeft, Info, ChevronRight,
  Stethoscope, BedDouble, GraduationCap, Wallet, Database, Download, Activity, Zap,
} from "lucide-react";
import { InfoTip } from "@/components/pawkt/HelpTip";

/* ------------------------------------------------------------ shared bits */
const CMP_TXT = { mom: "vs last month", yoy: "vs last year" };
const PLAIN_METRIC = { revenue: "Money received", net_revenue: "Money kept after refunds", contribution_margin: "Amount left after direct costs", gmv: "Money received (before refunds)" };
const fmtINR = (n) => { const x = Number(n || 0); return `₹${x.toLocaleString("en-IN", { minimumFractionDigits: Number.isInteger(x) ? 0 : 2, maximumFractionDigits: 2 })}`; };
const fmtNum = (n) => Number(n || 0).toLocaleString("en-IN");
const pct = (n) => (n === null || n === undefined ? "—" : `${n > 0 ? "+" : ""}${n}%`);

function useApi(path, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const reload = useCallback(() => {
    if (!path) { setLoading(false); return; }
    setLoading(true);
    api.get(path).then((r) => { setData(r.data); setError(null); setLoading(false); })
      .catch((e) => {
        // smooth post-MFA cold start: auth cookies may still be propagating -> retry once
        if (e?.response?.status === 401) {
          setTimeout(() => api.get(path)
            .then((r) => { setData(r.data); setError(null); })
            .catch((e2) => setError(e2)).finally(() => setLoading(false)), 900);
        } else { setError(e); setLoading(false); }
      });
  }, [path]);
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, deps.length ? deps : [path]);
  return { data, loading, error, reload };
}

function ErrorState({ error, onRetry }) {
  return (
    <div data-testid="in-error" className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-6 text-center">
      <AlertTriangle className="mx-auto h-6 w-6 text-rose-400" />
      <div className="mt-2 font-medium text-rose-200">Couldn’t load this data</div>
      <div className="mt-1 text-sm text-slate-400">{apiErr(error)}</div>
      <button data-testid="in-retry" onClick={onRetry} className="mt-4 inline-flex items-center gap-1.5 rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-200 hover:bg-slate-800">
        <RefreshCw className="h-4 w-4" /> Retry
      </button>
    </div>
  );
}
const Loading = () => <div className="text-slate-400 py-8" data-testid="in-loading">Loading intelligence…</div>;
const Empty = ({ label = "Insufficient data", hint }) => (
  <div data-testid="in-empty" className="rounded-xl border border-dashed border-slate-700 bg-slate-900/40 p-8 text-center">
    <Info className="mx-auto h-6 w-6 text-slate-500" />
    <div className="mt-2 font-medium text-slate-300">{label}</div>
    {hint && <div className="mt-1 text-sm text-slate-500">{hint}</div>}
  </div>
);

const Stat = ({ label, value, sub, testid, tone, help }) => (
  <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-4" data-testid={testid}>
    <div className="flex items-center gap-1 text-xs text-slate-400">{label}{help && <InfoTip k={help} testid={testid ? `help-${testid}` : undefined} />}</div>
    <div className={`mt-1 font-display text-2xl font-bold ${tone || ""}`}>{value}</div>
    {sub !== undefined && sub !== null && <div className="mt-0.5 text-xs text-slate-500">{sub}</div>}
  </div>
);
const Card = ({ title, children, right, testid }) => (
  <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-5" data-testid={testid}>
    <div className="flex items-center justify-between gap-2"><h3 className="font-semibold">{title}</h3>{right}</div>
    <div className="mt-3">{children}</div>
  </div>
);
function BarList({ items, labelKey, valueKey, money }) {
  if (!items || !items.length) return <div className="text-sm text-slate-500">No data</div>;
  const max = Math.max(1, ...items.map((i) => i[valueKey] || 0));
  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={i}>
          <div className="flex justify-between text-sm"><span className="text-slate-300 truncate">{it[labelKey] ?? "—"}</span>
            <span className="font-medium text-slate-200">{money ? fmtINR(it[valueKey]) : fmtNum(it[valueKey])}</span></div>
          <div className="mt-1 h-1.5 w-full rounded-full bg-slate-800"><div className="h-full rounded-full bg-indigo-500" style={{ width: `${((it[valueKey] || 0) / max) * 100}%` }} /></div>
        </div>
      ))}
    </div>
  );
}
function Spark({ series, money }) {
  if (!series || series.length < 2) return <div className="text-sm text-slate-500" data-testid="in-spark-insufficient">Insufficient history</div>;
  const vals = series.map((s) => s.value);
  const max = Math.max(...vals), min = Math.min(...vals);
  const pts = series.map((s, i) => {
    const x = (i / (series.length - 1)) * 100;
    const y = 100 - (max === min ? 50 : ((s.value - min) / (max - min)) * 90 + 5);
    return `${x},${y}`;
  }).join(" ");
  return (
    <div>
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="h-24 w-full"><polyline points={pts} fill="none" stroke="#818cf8" strokeWidth="2" vectorEffect="non-scaling-stroke" /></svg>
      <div className="mt-1 flex justify-between text-xs text-slate-500"><span>{series[0].month}</span><span>{money ? fmtINR(vals[vals.length - 1]) : fmtNum(vals[vals.length - 1])}</span></div>
    </div>
  );
}
const CompletenessBadge = ({ complete }) => (
  <span data-testid="in-completeness" className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${complete ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`}>
    {complete ? "Cost data complete" : "Cost data incomplete"}
  </span>
);

/* filters shared across screens — URL-backed so they persist through drill-down navigation */
function Filters({ bt, setBt, plan, setPlan, month, setMonth, comparison, setComparison }) {
  return (
    <div className="flex flex-wrap gap-2">
      <select data-testid="in-filter-type" value={bt} onChange={(e) => setBt(e.target.value)} className="rounded-lg border border-slate-800 bg-slate-900 px-3 py-2 text-sm">
        <option value="">All service types</option><option value="grooming">Grooming</option><option value="vet_clinic">Vet</option><option value="boarding">Boarding</option><option value="daycare">Daycare</option><option value="training">Training</option><option value="pet_store">Pet store</option><option value="pet_resort">Pet resort</option>
      </select>
      <select data-testid="in-filter-plan" value={plan} onChange={(e) => setPlan(e.target.value)} className="rounded-lg border border-slate-800 bg-slate-900 px-3 py-2 text-sm">
        <option value="">All plans</option><option value="free">Free</option><option value="starter">Starter</option><option value="growth">Growth</option><option value="multi-branch">Multi-Branch</option>
      </select>
      <input data-testid="in-filter-month" value={month} onChange={(e) => setMonth(e.target.value)} placeholder="Month YYYY-MM" className="w-32 rounded-lg border border-slate-800 bg-slate-900 px-3 py-2 text-sm" />
      {setComparison && (
        <select data-testid="in-filter-comparison" value={comparison} onChange={(e) => setComparison(e.target.value)} className="rounded-lg border border-slate-800 bg-slate-900 px-3 py-2 text-sm">
          <option value="none">No comparison</option><option value="mom">vs previous month</option><option value="yoy">vs same month last year</option>
        </select>
      )}
    </div>
  );
}
const enc = encodeURIComponent;
function useFilters() {
  const [sp, setSp] = useSearchParams();
  const bt = sp.get("business_type") || "", plan = sp.get("plan") || "", month = sp.get("month") || "";
  const state = sp.get("state") || "", city = sp.get("city") || "";
  const branch = sp.get("branch_id") || "", tenant = sp.get("tenant_id") || "";
  const comparison = sp.get("comparison") || "mom";
  const range = sp.get("range") || "6m";
  const upd = (k, v) => { const n = new URLSearchParams(sp); v ? n.set(k, v) : n.delete(k); setSp(n); };
  const qs = `business_type=${enc(bt)}&plan=${enc(plan)}`;
  const vqs = `business_type=${enc(bt)}&plan=${enc(plan)}&month=${enc(month)}&state=${enc(state)}&city=${enc(city)}&branch_id=${enc(branch)}&tenant_id=${enc(tenant)}`;
  // full current query string, with optional overrides, used to carry filters across navigation
  const carry = (extra = {}) => {
    const n = new URLSearchParams(sp);
    Object.entries(extra).forEach(([k, v]) => (v === null || v === "" ? n.delete(k) : n.set(k, v)));
    const s = n.toString();
    return s ? `?${s}` : "";
  };
  return { sp, bt, plan, month, state, city, branch, tenant, comparison, range,
    setBt: (v) => upd("business_type", v), setPlan: (v) => upd("plan", v), setMonth: (v) => upd("month", v),
    setComparison: (v) => upd("comparison", v), setRange: (v) => upd("range", v), setParam: upd,
    qs, vqs, carry, node: <Filters bt={bt} setBt={(v) => upd("business_type", v)} plan={plan} setPlan={(v) => upd("plan", v)} month={month} setMonth={(v) => upd("month", v)} comparison={comparison} setComparison={(v) => upd("comparison", v)} /> };
}
function Header({ title, subtitle, right }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div><h1 className="font-display text-2xl font-bold">{title}</h1>{subtitle && <p className="text-sm text-slate-400 mt-1">{subtitle}</p>}</div>
      {right}
    </div>
  );
}
/* clickable drill-down breadcrumbs */
function Breadcrumbs({ items }) {
  const navigate = useNavigate();
  return (
    <nav data-testid="in-breadcrumbs" className="mt-3 flex flex-wrap items-center gap-1 text-sm">
      {items.map((it, i) => (
        <span key={i} className="flex items-center gap-1">
          {i > 0 && <ChevronRight className="h-3.5 w-3.5 text-slate-600" />}
          {it.to && i < items.length - 1 ? (
            <button data-testid={`in-crumb-${i}`} onClick={() => navigate(it.to)} className="rounded px-1.5 py-0.5 text-slate-400 hover:bg-slate-800/60 hover:text-slate-100">{it.label}</button>
          ) : (<span className="rounded px-1.5 py-0.5 font-medium text-indigo-300">{it.label}</span>)}
        </span>
      ))}
    </nav>
  );
}
/* descriptive revenue-mix donut (inline SVG, no chart dependency, never ranks) */
const MIX_COLORS = { veterinary: "#38bdf8", grooming: "#a78bfa", boarding: "#34d399", training: "#fbbf24", pet_store: "#f472b6", other: "#64748b" };
function RevenueDonut({ mix, onSlice }) {
  const total = mix.reduce((s, m) => s + (m.revenue || 0), 0);
  if (!total) return <Empty label="No revenue recorded" hint="Revenue mix appears once captured payments exist." />;
  let acc = 0; const R = 42, C = 2 * Math.PI * R;
  return (
    <div className="flex flex-col items-center gap-4 sm:flex-row" data-testid="in-revenue-donut">
      <svg viewBox="0 0 100 100" className="h-40 w-40 -rotate-90">
        {mix.filter((m) => m.revenue > 0).map((m) => {
          const frac = m.revenue / total, dash = `${frac * C} ${C}`, off = -acc * C; acc += frac;
          return <circle key={m.vertical} cx="50" cy="50" r={R} fill="none" stroke={MIX_COLORS[m.vertical]} strokeWidth="14" strokeDasharray={dash} strokeDashoffset={off} className={onSlice ? "cursor-pointer" : ""} onClick={() => onSlice && onSlice(m.vertical)} />;
        })}
      </svg>
      <div className="flex-1 space-y-1.5">
        {mix.map((m) => (
          <div key={m.vertical} data-testid={`in-mix-${m.vertical}`} className={`flex items-center justify-between gap-3 rounded-md px-2 py-1 text-sm ${onSlice && m.vertical !== "other" && m.vertical !== "pet_store" ? "cursor-pointer hover:bg-slate-800/50" : ""}`} onClick={() => onSlice && m.vertical !== "other" && m.vertical !== "pet_store" && onSlice(m.vertical)}>
            <span className="flex items-center gap-2 text-slate-300"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: MIX_COLORS[m.vertical] }} />{m.label}</span>
            <span className="text-slate-200">{fmtINR(m.revenue)} <span className="text-slate-500">· {m.pct}%</span></span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ 6G-4 time intelligence */
const STATUS_COPY = {
  NO_DATA: "No comparable data", INSUFFICIENT_HISTORY: "Insufficient history for comparison",
  NOT_SUPPORTED: "Comparison not supported", ZERO_BASE: "Percentage unavailable (previous period was 0)",
};
/* compact sub-label string for a comparison in any status (used on Stat cards) */
function cmpSub(cmp) {
  if (!cmp) return undefined;
  if (cmp.status === "AVAILABLE") return `${cmp.percentage_change > 0 ? "+" : ""}${cmp.percentage_change}% ${CMP_TXT[cmp.comparison_type] || cmp.comparison_type || ""}`;
  if (cmp.status === "ZERO_BASE") return "% unavailable (prev 0)";
  if (cmp.status === "NO_DATA") return "No comparable data";
  if (cmp.status === "INSUFFICIENT_HISTORY") return "Insufficient history";
  return undefined;
}
function useComparison(enabled, { vertical, metrics, month, comparison, tenant_id, branch_id, state, city, business_type, plan }) {
  const [map, setMap] = useState(null);
  const q = new URLSearchParams();
  q.set("metrics", metrics.join(","));
  q.set("comparison", comparison);
  if (vertical) q.set("vertical", vertical);
  if (month) q.set("month", month);
  if (tenant_id) q.set("tenant_id", tenant_id);
  if (branch_id) q.set("branch_id", branch_id);
  if (state) q.set("state", state);
  if (city) q.set("city", city);
  if (business_type) q.set("business_type", business_type);
  if (plan) q.set("plan", plan);
  const key = q.toString();
  useEffect(() => {
    if (!enabled) { setMap(null); return; }
    let alive = true;
    api.get(`/platform/intel/time-comparison?${key}`).then((r) => {
      if (!alive) return;
      const m = {}; (r.data.items || []).forEach((it) => (m[it.metric] = it));
      setMap({ meta: r.data, byMetric: m });
    }).catch(() => alive && setMap(null));
    return () => { alive = false; };
  }, [enabled, key]);
  return map;
}
/* neutral (non-judgemental) period delta — never green=good/red=bad */
function Delta({ cmp, money }) {
  if (!cmp) return null;
  const st = cmp.status;
  if (st !== "AVAILABLE" && st !== "ZERO_BASE") {
    return <div data-testid="in-delta" className="mt-1 text-[11px] text-slate-500">{STATUS_COPY[st] || "—"}</div>;
  }
  const ac = cmp.absolute_change ?? 0;
  const up = ac > 0, down = ac < 0;
  const arrow = up ? "↑" : down ? "↓" : "→";
  const absTxt = `${ac > 0 ? "+" : ""}${money ? fmtINR(ac) : fmtNum(ac)}`;
  return (
    <div data-testid="in-delta" className="mt-1 flex flex-wrap items-center gap-x-2 text-[11px] text-slate-400">
      <span className="text-slate-300">{arrow} {absTxt}</span>
      {st === "ZERO_BASE" ? <span className="text-slate-500">% unavailable (prev 0)</span>
        : <span className="text-slate-500">{cmp.percentage_change > 0 ? "+" : ""}{cmp.percentage_change}% {CMP_TXT[cmp.comparison_type] || cmp.comparison_type}</span>}
    </div>
  );
}
/* full period comparison card for a single headline metric */
function PeriodComparison({ cmp, label, money }) {
  if (!cmp) return null;
  const st = cmp.status;
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-4" data-testid={`in-cmp-${cmp.metric}`}>
      <div className="text-xs text-slate-400">{label}</div>
      {(st === "AVAILABLE" || st === "ZERO_BASE") ? (<>
        <div className="mt-1 font-display text-xl font-bold">{money ? fmtINR(cmp.current_value) : fmtNum(cmp.current_value)}</div>
        <div className="text-xs text-slate-500">{cmp.current_period} · vs {cmp.comparison_period}: {money ? fmtINR(cmp.comparison_value) : fmtNum(cmp.comparison_value)}</div>
        <Delta cmp={cmp} money={money} />
      </>) : (
        <div className="mt-1 text-sm text-slate-400">{STATUS_COPY[st] || "—"}</div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------ 6G-5 historical trend explorer */
const TREND_CATALOG = {
  overview: [{ k: "revenue", label: "Money received", vertical: "finance", money: true }, { k: "gmv", label: "Money received (before refunds)", money: true }, { k: "bookings_created", label: "Bookings" }, { k: "new_customers", label: "New customers" }, { k: "new_pets", label: "New pets" }, { k: "contribution_margin", label: "Amount left after direct costs", vertical: "finance", money: true }, { k: "inventory_consumed", label: "Inventory consumption" }],
  finance: [{ k: "revenue", label: "Money received", vertical: "finance", money: true }, { k: "net_revenue", label: "Money kept after refunds", vertical: "finance", money: true }, { k: "refunds", label: "Refunds", vertical: "finance", money: true }, { k: "contribution_margin", label: "Amount left after direct costs", vertical: "finance", money: true }, { k: "operating_result", label: "Operating result", vertical: "finance", money: true }],
  veterinary: [{ k: "encounters", label: "Encounters", vertical: "veterinary" }, { k: "completed_encounters", label: "Completed", vertical: "veterinary" }, { k: "emergency_encounters", label: "Emergencies", vertical: "veterinary" }, { k: "vaccinations", label: "Vaccinations", vertical: "veterinary" }, { k: "recorded_diagnoses", label: "Recorded conditions", vertical: "veterinary" }, { k: "unique_pets", label: "Unique pets", vertical: "veterinary" }],
  boarding: [{ k: "reservations", label: "Reservations", vertical: "boarding" }, { k: "check_ins", label: "Check-ins", vertical: "boarding" }, { k: "check_outs", label: "Check-outs", vertical: "boarding" }, { k: "unique_pets", label: "Unique pets", vertical: "boarding" }],
  training: [{ k: "packages", label: "Packages", vertical: "training" }, { k: "sessions", label: "Sessions", vertical: "training" }, { k: "completed_sessions", label: "Completed", vertical: "training" }, { k: "no_show_sessions", label: "No-shows", vertical: "training" }],
  grooming: [{ k: "tickets", label: "Tickets", vertical: "grooming" }, { k: "completed_services", label: "Completed services", vertical: "grooming" }, { k: "unique_pets", label: "Unique pets", vertical: "grooming" }],
  business: [{ k: "revenue", label: "Money received", vertical: "finance", money: true }, { k: "net_revenue", label: "Money kept after refunds", vertical: "finance", money: true }, { k: "contribution_margin", label: "Amount left after direct costs", vertical: "finance", money: true }, { k: "gmv", label: "Money received (before refunds)", money: true }, { k: "bookings_created", label: "Bookings" }],
};
TREND_CATALOG.all = [...TREND_CATALOG.overview, ...TREND_CATALOG.finance, ...TREND_CATALOG.veterinary, ...TREND_CATALOG.boarding, ...TREND_CATALOG.training, ...TREND_CATALOG.grooming]
  .filter((m, i, a) => a.findIndex((x) => x.k === m.k && (x.vertical || "") === (m.vertical || "")) === i);
const RANGE_OPTS = [{ v: "3m", l: "3 mo" }, { v: "6m", l: "6 mo" }, { v: "12m", l: "12 mo" }];

/* lightweight inline-SVG/CSS bar chart — real monthly values, explicit gaps for missing months */
function TrendChart({ points, money, onMonth }) {
  const vals = points.filter((p) => p.status === "AVAILABLE").map((p) => p.value);
  const max = Math.max(1, ...vals);
  return (
    <div className="overflow-x-auto pb-1" data-testid="in-trend-chart" role="img" aria-label="Historical trend chart">
      <div className="flex items-end gap-1.5" style={{ minWidth: `${points.length * 40}px`, height: "160px" }}>
        {points.map((p) => (
          <div key={p.period} className="flex flex-1 flex-col items-center justify-end gap-1" style={{ minWidth: "30px", height: "100%" }}>
            {p.status === "AVAILABLE" ? (
              <button data-testid={`in-trend-bar-${p.period}`} title={`${p.period}: ${money ? fmtINR(p.value) : fmtNum(p.value)}`} onClick={() => onMonth && onMonth(p.period)}
                className="w-full rounded-t bg-indigo-500/70 transition-colors hover:bg-indigo-400" style={{ height: `${Math.max(3, (p.value / max) * 130)}px` }} />
            ) : (
              <div data-testid={`in-trend-gap-${p.period}`} title={`${p.period}: no data`} className="flex w-full items-end justify-center" style={{ height: "130px" }}>
                <span className="border-b border-dashed border-slate-600 pb-0.5 text-[10px] text-slate-600">—</span>
              </div>
            )}
            <span className="text-[10px] text-slate-500">{p.period.slice(2)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
function TrendTable({ points, money, label }) {
  return (
    <div className="mt-4 overflow-x-auto rounded-lg border border-slate-800" data-testid="in-trend-table">
      <table className="w-full text-sm"><caption className="sr-only">{label} historical values</caption>
        <thead className="bg-slate-900/70 text-left text-xs uppercase text-slate-500"><tr><th className="px-3 py-2 font-medium">Month</th><th className="px-3 py-2 text-right font-medium">{label}</th><th className="px-3 py-2 font-medium">Status</th></tr></thead>
        <tbody>{points.map((p) => (
          <tr key={p.period} className="border-t border-slate-800">
            <td className="px-3 py-2">{p.period}</td>
            <td className="px-3 py-2 text-right">{p.status === "AVAILABLE" ? (money ? fmtINR(p.value) : fmtNum(p.value)) : "—"}</td>
            <td className="px-3 py-2 text-xs text-slate-500">{p.status === "AVAILABLE" ? "Available" : "No data"}</td>
          </tr>))}
        </tbody></table>
    </div>
  );
}
function HistoricalTrends({ title = "Historical trends", catalog, vertical, tenant_id, branch_id, urlMetric, testid = "in-trends" }) {
  const f = useFilters();
  const navigate = useNavigate();
  const [localMetric, setLocalMetric] = useState(catalog[0].k);
  const metric = urlMetric ? (f.sp.get("metric") || catalog[0].k) : localMetric;
  const setMetric = urlMetric ? (v) => f.setParam("metric", v) : setLocalMetric;
  const sel = catalog.find((c) => c.k === metric) || catalog[0];
  const ver = sel.vertical || vertical || "";
  const q = new URLSearchParams();
  q.set("metric", sel.k); q.set("range", f.range);
  if (ver) q.set("vertical", ver);
  const tid = tenant_id || f.tenant; if (tid) q.set("tenant_id", tid);
  const bid = branch_id || f.branch; if (bid) q.set("branch_id", bid);
  if (f.state) q.set("state", f.state);
  if (f.city) q.set("city", f.city);
  if (f.bt) q.set("business_type", f.bt);
  if (f.plan) q.set("plan", f.plan);
  const key = q.toString();
  const { data, loading, error, reload } = useApi(`/platform/intel/time-series?${key}`, [key]);
  return (
    <div className="mt-6 rounded-xl border border-slate-800 bg-slate-900/40 p-4" data-testid={testid}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 font-semibold"><Activity className="h-4 w-4 text-indigo-300" />{title}</h3>
        <div className="flex flex-wrap gap-2">
          <select data-testid={`${testid}-metric`} value={metric} onChange={(e) => setMetric(e.target.value)} className="rounded-lg border border-slate-800 bg-slate-900 px-3 py-1.5 text-sm">
            {catalog.map((c) => <option key={`${c.k}-${c.vertical || ""}`} value={c.k}>{c.label}</option>)}
          </select>
          <div className="flex overflow-hidden rounded-lg border border-slate-800">
            {RANGE_OPTS.map((r) => (
              <button key={r.v} data-testid={`${testid}-range-${r.v}`} onClick={() => f.setRange(r.v)} className={`px-3 py-1.5 text-sm ${f.range === r.v ? "bg-indigo-500/20 text-indigo-200" : "text-slate-400 hover:bg-slate-800"}`}>{r.l}</button>
            ))}
          </div>
        </div>
      </div>
      {data && <DataTrust trust={data.data_trust} coverage={data.status === "NOT_SUPPORTED" ? "NOT_SUPPORTED" : (data.status === "AVAILABLE" ? "AVAILABLE" : data.status)} />}
      {error ? <div className="mt-3"><ErrorState error={error} onRetry={reload} /></div>
        : loading || !data ? <Loading />
        : data.status === "NOT_SUPPORTED" ? <div className="mt-3"><Empty label="Metric not supported for trends" /></div>
        : data.status === "NO_DATA" ? <div className="mt-3"><Empty label="No historical data available" /></div>
        : (<>
          {data.status === "INSUFFICIENT_HISTORY" && <div className="mt-3 text-xs text-amber-300" data-testid={`${testid}-insufficient`}>Insufficient historical data for a multi-period trend.</div>}
          <div className="mt-4"><TrendChart points={data.points} money={data.unit === "INR"} onMonth={(p) => navigate({ search: f.carry({ month: p }) })} /></div>
          <TrendTable points={data.points} money={data.unit === "INR"} label={sel.label} />
          {data.attribution && data.attribution.attributed_pct !== null && <div className="mt-2 text-xs text-slate-500" data-testid={`${testid}-attribution`}>Revenue attribution coverage: {data.attribution.attributed_pct}% attributed · {fmtINR(data.attribution.other)} Other / Unclassified (not missing money)</div>}
        </>)}
    </div>
  );
}
export function IntelTrends() {
  const f = useFilters();
  return (
    <div data-testid="in-trends-page">
      <Header title="Historical Trend Explorer" subtitle="Descriptive monthly trajectory of a metric across your filters. Read-only — no forecasting." right={<div className="flex flex-col items-end gap-2">{f.node}<ExploreExport f={f} /></div>} />
      <Breadcrumbs items={[{ label: "Intelligence", to: "/platform-admin/intelligence" }, { label: "Trends" }]} />
      <HistoricalTrends title="Metric trajectory" catalog={TREND_CATALOG.all} urlMetric testid="in-trends" />
    </div>
  );
}

/* ------------------------------------------------------------ 6G-6 comparative intelligence */
const BM_METRICS = [
  { k: "revenue", label: "Money received", vertical: "finance", money: true },
  { k: "net_revenue", label: "Money kept after refunds", vertical: "finance", money: true },
  { k: "refunds", label: "Refunds", vertical: "finance", money: true },
  { k: "contribution_margin", label: "Amount left after direct costs", vertical: "finance", money: true },
  { k: "bookings_created", label: "Bookings", money: false },
  { k: "new_customers", label: "New customers", money: false },
  { k: "revenue_per_booking", label: "Revenue per booking", money: true },
  { k: "avg_transaction_value", label: "Avg transaction value", money: true },
];
function DistributionBar({ stats, selected, money }) {
  const lo = Math.min(stats.min, selected?.available ? selected.value : stats.min);
  const hi = Math.max(stats.max, selected?.available ? selected.value : stats.max);
  const span = hi - lo || 1;
  const pos = (v) => `${Math.max(0, Math.min(100, ((v - lo) / span) * 100))}%`;
  const fmt = (v) => (money ? fmtINR(v) : fmtNum(v));
  return (
    <div className="mt-4" data-testid="in-bm-distribution">
      <div className="relative h-2 rounded-full bg-slate-800">
        <div className="absolute h-2 rounded-full bg-indigo-500/40" style={{ left: pos(stats.p25), width: `${Math.max(2, ((stats.p75 - stats.p25) / span) * 100)}%` }} />
        {["p25", "median", "p75"].map((k) => (
          <div key={k} className="absolute -top-1 h-4 w-0.5 bg-indigo-300" style={{ left: pos(stats[k]) }} title={`${k}: ${fmt(stats[k])}`} />
        ))}
        {selected?.available && (
          <div data-testid="in-bm-marker" className="absolute -top-2 flex flex-col items-center" style={{ left: pos(selected.value), transform: "translateX(-50%)" }}>
            <div className="h-6 w-0.5 bg-emerald-300" /><span className="mt-0.5 whitespace-nowrap text-[10px] text-emerald-300">▲ {fmt(selected.value)}</span>
          </div>
        )}
      </div>
      <div className="mt-6 flex justify-between text-[11px] text-slate-500">
        <span>Lower range (P25) {fmt(stats.p25)}</span><span>Middle (median) {fmt(stats.median)}</span><span>Upper range (P75) {fmt(stats.p75)}</span><InfoTip k="quartiles" />
      </div>
    </div>
  );
}
export function IntelBenchmark() {
  const f = useFilters();
  const navigate = useNavigate();
  const metric = f.sp.get("metric") || "revenue";
  const period = f.sp.get("period") || f.month || "";
  const bvertical = f.sp.get("bvertical") || "";
  const sel = BM_METRICS.find((m) => m.k === metric) || BM_METRICS[0];
  const q = new URLSearchParams();
  q.set("metric", metric);
  if (period) q.set("period", period);
  if (bvertical) q.set("vertical", bvertical);
  if (f.state) q.set("state", f.state);
  if (f.city) q.set("city", f.city);
  if (f.plan) q.set("plan", f.plan);
  if (f.tenant) q.set("tenant_id", f.tenant);
  const key = q.toString();
  const { data, loading, error, reload } = useApi(`/platform/intel/benchmark?${key}`, [key]);
  const money = sel.money;
  const fmtV = (v) => (v == null ? "—" : money ? fmtINR(v) : fmtNum(v));
  return (
    <div data-testid="in-benchmark-page">
      <Header title="Compare with similar businesses" subtitle="How a business compares with comparable businesses. Privacy-safe; not a ranking or score." right={<div className="flex flex-col items-end gap-2">{f.node}<ExploreExport f={f} /></div>} />
      <Breadcrumbs items={[{ label: "Intelligence", to: "/platform-admin/intelligence" }, { label: "Comparative" }]} />
      <div className="mt-4 flex flex-wrap gap-2">
        <select data-testid="in-bm-metric" value={metric} onChange={(e) => f.setParam("metric", e.target.value)} className="rounded-lg border border-slate-800 bg-slate-900 px-3 py-2 text-sm">
          {BM_METRICS.map((m) => <option key={m.k} value={m.k}>{m.label}</option>)}
        </select>
        <input data-testid="in-bm-period" value={period} onChange={(e) => f.setParam("period", e.target.value)} placeholder="Period YYYY-MM" className="w-36 rounded-lg border border-slate-800 bg-slate-900 px-3 py-2 text-sm" />
        <select data-testid="in-bm-vertical" value={bvertical} onChange={(e) => f.setParam("bvertical", e.target.value)} className="rounded-lg border border-slate-800 bg-slate-900 px-3 py-2 text-sm">
          <option value="">All service types</option><option value="veterinary">Veterinary</option><option value="grooming">Grooming</option><option value="boarding">Boarding</option><option value="training">Training</option><option value="pet_store">Pet store</option>
        </select>
      </div>
      {data && <DataTrust trust={data.data_trust} coverage={data.status === "AVAILABLE" ? "AVAILABLE" : data.status} />}
      {error ? <div className="mt-4"><ErrorState error={error} onRetry={reload} /></div>
        : loading || !data ? <Loading />
        : data.status === "NOT_SUPPORTED" ? <div className="mt-4"><Empty label="Metric not supported for benchmarking" /></div>
        : data.status === "INSUFFICIENT_COHORT" ? (
          <div className="mt-4 rounded-xl border border-amber-500/30 bg-amber-500/5 p-5" data-testid="in-bm-insufficient">
            <div className="flex items-start gap-2 text-amber-200"><AlertTriangle className="h-5 w-5 shrink-0" /><div>
              <div className="font-semibold">Not enough comparable businesses</div>
              <p className="mt-1 text-sm">{data.message}</p>
              <p className="mt-2 text-xs text-slate-400">Broaden the cohort explicitly to compare — e.g. widen geography or remove the vertical filter. The system never broadens automatically.</p>
              {f.city && <button data-testid="in-bm-broaden-geo" onClick={() => f.setParam("city", "")} className="mt-3 rounded-lg border border-slate-700 px-3 py-1.5 text-xs hover:bg-slate-800">Broaden to state</button>}
            </div></div>
          </div>
        ) : (
          <div className="mt-4 grid gap-5 lg:grid-cols-3">
            <div className="lg:col-span-2 rounded-xl border border-slate-800 bg-slate-900/50 p-5">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div><div className="text-xs text-slate-400">Your business</div><div className="font-display text-2xl font-bold" data-testid="in-bm-selected">{data.selected_business?.available ? fmtV(data.selected_business.value) : "No data for this business"}</div></div>
                <div className="text-right"><div className="flex items-center justify-end gap-1 text-xs text-slate-400">Middle value of comparable businesses<InfoTip k="median" /></div><div className="font-display text-xl font-bold text-indigo-300" data-testid="in-bm-median">{fmtV(data.statistics.median)}</div></div>
              </div>
              {data.statistics.band && <div className="mt-2 text-sm text-slate-300" data-testid="in-bm-band">Your value falls within the <span className="font-semibold text-emerald-300">{data.statistics.band}</span>{data.statistics.percentile != null && ` (≈ P${data.statistics.percentile})`}.</div>}
              <DistributionBar stats={data.statistics} selected={data.selected_business} money={money} />
              <div className="mt-8 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
                <div><div className="text-slate-500">Min</div><div className="font-semibold">{fmtV(data.statistics.min)}</div></div>
                <div><div className="text-slate-500">P25</div><div className="font-semibold">{fmtV(data.statistics.p25)}</div></div>
                <div><div className="text-slate-500">P75</div><div className="font-semibold">{fmtV(data.statistics.p75)}</div></div>
                <div><div className="text-slate-500">Max</div><div className="font-semibold">{fmtV(data.statistics.max)}</div></div>
              </div>
            </div>
            <div className="space-y-4">
              <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-4" data-testid="in-bm-cohort">
                <h3 className="flex items-center gap-1 font-semibold">Comparable businesses<InfoTip k="peer_group" /></h3>
                <div className="mt-2 text-sm text-slate-300">{fmtNum(data.statistics.cohort_size)} comparable businesses</div>
                <dl className="mt-3 space-y-1 text-xs text-slate-400">
                  <div>Metric: <span className="text-slate-200">{sel.label}</span></div>
                  <div>Period: <span className="text-slate-200">{data.period}</span></div>
                  <div>Vertical: <span className="text-slate-200">{bvertical || "All"}</span></div>
                  <div>Geography: <span className="text-slate-200">{f.city || f.state || "India"}</span></div>
                  <div>Plan: <span className="text-slate-200">{f.plan || "All"}</span></div>
                </dl>
              </div>
              <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-4" data-testid="in-bm-how">
                <h3 className="font-semibold">How this comparison is calculated</h3>
                <p className="mt-2 text-xs text-slate-400">{data.cohort.definition.basis}</p>
                <p className="mt-1 text-xs text-slate-400">{data.cohort.definition.method}</p>
                {data.cohort.quality && <p className="mt-1 text-xs text-slate-500">{data.cohort.quality.with_usable_metric} of {data.cohort.quality.cohort_candidates} businesses had usable data ({data.cohort.quality.excluded_missing_metric} excluded for missing data).</p>}
                {money && <p className="mt-1 text-xs text-slate-500">"Amount left after direct costs" is not profit. Money received uses captured payments.</p>}
              </div>
              <div className="flex flex-wrap gap-2">
                <ExploreExport f={f} />
                {f.tenant && <button data-testid="in-bm-view-business" onClick={() => navigate(`/platform-admin/intelligence/business/${f.tenant}${f.carry()}`)} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 px-3 py-2 text-sm hover:bg-slate-800"><Building2 className="h-4 w-4" /> View business</button>}
                <button data-testid="in-bm-view-trend" onClick={() => navigate(`/platform-admin/intelligence/trends${f.carry({ metric })}`)} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 px-3 py-2 text-sm hover:bg-slate-800"><Activity className="h-4 w-4" /> View trend</button>
              </div>
            </div>
          </div>
        )}
    </div>
  );
}

/* ------------------------------------------------------------ 6G-7 deterministic anomaly detection */
const ANOMALY_BASELINE_OPTS = [{ v: "3m", l: "3 mo" }, { v: "6m", l: "6 mo" }, { v: "12m", l: "12 mo" }];
const DIR_GLYPH = { UP: "↑", DOWN: "↓", FLAT: "→", NONE: "" };
const ANOMALY_STATUS_COPY = {
  UNUSUAL: "Unusual movement", NORMAL: "Within recent range",
  INSUFFICIENT_HISTORY: "Insufficient history", NO_DATA: "No data recorded",
  NOT_SUPPORTED: "Not supported", DATA_STALE: "Data stale", DISABLED: "Disabled",
};
const isAnomalyUnusual = (a) => a.status === "UNUSUAL" || (a.status === "DATA_STALE" && a.underlying_status === "UNUSUAL");
const anomVal = (a, v) => (v == null ? "—" : a.unit === "INR" ? fmtINR(v) : fmtNum(v));

/* neutral severity chip — HIGH is a LARGER statistical deviation, never "bad" */
function SeverityChip({ severity }) {
  if (severity === "NORMAL") return null;
  const style = severity === "HIGH" ? "bg-violet-500/15 text-violet-200 border-violet-500/30"
    : "bg-amber-500/15 text-amber-200 border-amber-500/30";
  return <span data-testid="in-anom-severity" className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${style}`}>{severity === "HIGH" ? "Larger deviation" : "Notable deviation"}</span>;
}

function AnomalyCard({ a, onTrend, onBusiness, f }) {
  const [showEv, setShowEv] = useState(false);
  const unusual = isAnomalyUnusual(a);
  const hasVals = ["UNUSUAL", "NORMAL", "DATA_STALE"].includes(a.status) && a.current_value != null && a.historical_median != null;
  const canEvidence = Array.isArray(a.baseline) && a.baseline.length > 0;
  return (
    <div data-testid={`in-anom-card-${a.metric}`} className={`rounded-xl border p-4 ${unusual ? "border-amber-500/40 bg-amber-500/5" : "border-slate-800 bg-slate-900/50"}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-sm font-medium text-slate-200">{a.label || a.metric}</div>
        <div className="flex items-center gap-1.5">
          {a.direction && a.direction !== "NONE" && <span data-testid={`in-anom-dir-${a.metric}`} className="text-xs text-slate-400">{DIR_GLYPH[a.direction]} {a.direction}</span>}
          <SeverityChip severity={a.severity} />
        </div>
      </div>
      <div data-testid={`in-anom-status-${a.metric}`} className={`mt-1 text-xs font-medium ${unusual ? "text-amber-300" : "text-slate-500"}`}>{ANOMALY_STATUS_COPY[a.status] || a.status}</div>
      {hasVals ? (<>
        <div className="mt-2 font-display text-xl font-bold" data-testid={`in-anom-current-${a.metric}`}>{anomVal(a, a.current_value)}</div>
        <div className="text-xs text-slate-500">Usual level (middle value) {anomVal(a, a.historical_median)} · based on {a.baseline_count} months</div>
        <div className="mt-1 text-xs text-slate-400">{a.zero_base ? "Change % unavailable (usual level is 0)" : `${a.percentage_change > 0 ? "+" : ""}${a.percentage_change}% vs usual level`}{a.modified_z != null && ` · unusualness score ${a.modified_z}`}</div>
      </>) : (
        <div className="mt-2 text-sm text-slate-400">{a.message || "—"}</div>
      )}
      {a.status === "DATA_STALE" && <div className="mt-2 text-[11px] text-amber-300" data-testid={`in-anom-stale-${a.metric}`}>{a.message}</div>}
      <div className="mt-3 flex flex-wrap gap-2">
        {onTrend && <button data-testid={`in-anom-trend-${a.metric}`} onClick={() => onTrend(a)} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 px-2.5 py-1.5 text-xs hover:bg-slate-800"><Activity className="h-3.5 w-3.5" /> View trend</button>}
        {canEvidence && <button data-testid={`in-anom-evidence-${a.metric}`} onClick={() => setShowEv((s) => !s)} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 px-2.5 py-1.5 text-xs hover:bg-slate-800"><Info className="h-3.5 w-3.5" /> {showEv ? "Hide" : "View"} evidence</button>}
        {onBusiness && <button data-testid={`in-anom-business-${a.metric}`} onClick={() => onBusiness(a)} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 px-2.5 py-1.5 text-xs hover:bg-slate-800"><Building2 className="h-3.5 w-3.5" /> View business</button>}
      </div>
      {showEv && canEvidence && (
        <div className="mt-2 rounded-lg border border-slate-800 bg-slate-950/60 p-3 text-xs" data-testid={`in-anom-evidence-panel-${a.metric}`}>
          <div className="text-slate-400">Statistical evidence · method {a.method} · baseline {a.baseline_period?.start}–{a.baseline_period?.end}</div>
          <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-0.5 sm:grid-cols-3">
            {a.baseline.map((p) => <div key={p.period} className="flex justify-between"><span className="text-slate-500">{p.period}</span><span className="text-slate-300">{anomVal(a, p.value)}</span></div>)}
          </div>
          <div className="mt-2 text-slate-400">Selected {a.period}: <span className="text-slate-200">{anomVal(a, a.current_value)}</span> · median {anomVal(a, a.historical_median)}{a.modified_z != null && ` · modified z ${a.modified_z}`}</div>
          <div className="mt-1 text-[11px] text-slate-500">Statistical evidence only — describes the movement, not its cause.</div>
        </div>
      )}
    </div>
  );
}

/* compact "Unusual movements" section reused on Overview / Business / Branch details */
export function UnusualMovements({ tenant_id, branch_id, vertical, title = "Unusual movements", testid = "in-unusual" }) {
  const f = useFilters();
  const navigate = useNavigate();
  const q = new URLSearchParams();
  q.set("baseline", "12m");
  if (f.month) q.set("period", f.month);
  if (tenant_id) q.set("tenant_id", tenant_id);
  if (branch_id) q.set("branch_id", branch_id);
  if (vertical) q.set("vertical", vertical);
  if (!tenant_id) { if (f.bt) q.set("business_type", f.bt); if (f.plan) q.set("plan", f.plan); if (f.state) q.set("state", f.state); if (f.city) q.set("city", f.city); }
  const key = q.toString();
  const { data } = useApi(`/platform/intel/anomalies?${key}`, [key]);
  if (!data) return null;
  const unusual = (data.items || []).filter(isAnomalyUnusual);
  const goTrend = (a) => {
    const extra = { metric: a.metric, month: a.period };
    if (tenant_id) extra.tenant_id = tenant_id;
    if (branch_id) extra.branch_id = branch_id;
    navigate(`/platform-admin/intelligence/trends${f.carry(extra)}`);
  };
  return (
    <div className="mt-5 rounded-xl border border-slate-800 bg-slate-900/50 p-5" data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 font-semibold"><Zap className="h-4 w-4 text-amber-300" />{title}</h3>
        <span className="text-xs text-slate-500">own-history baseline · descriptive</span>
      </div>
      {unusual.length === 0 ? (
        <div className="mt-2 text-sm text-slate-500" data-testid={`${testid}-none`}>No unusual movements detected against the recent historical pattern.</div>
      ) : (<>
        <div className="mt-1 text-sm text-slate-300" data-testid={`${testid}-count`}>{unusual.length} metric{unusual.length > 1 ? "s" : ""} outside {unusual.length > 1 ? "their" : "its"} recent historical pattern</div>
        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {unusual.map((a) => <AnomalyCard key={a.metric} a={a} onTrend={goTrend} f={f} />)}
        </div>
      </>)}
    </div>
  );
}

export function IntelAnomalies() {
  const f = useFilters();
  const navigate = useNavigate();
  const baseline = f.sp.get("abaseline") || "12m";
  const avertical = f.sp.get("avertical") || "";
  const period = f.sp.get("period") || f.month || "";
  const q = new URLSearchParams();
  q.set("baseline", baseline);
  if (period) q.set("period", period);
  if (avertical) q.set("vertical", avertical);
  if (f.tenant) q.set("tenant_id", f.tenant);
  if (f.branch) q.set("branch_id", f.branch);
  if (f.state) q.set("state", f.state);
  if (f.city) q.set("city", f.city);
  if (f.bt) q.set("business_type", f.bt);
  if (f.plan) q.set("plan", f.plan);
  const key = q.toString();
  const { data, loading, error, reload } = useApi(`/platform/intel/anomalies?${key}`, [key]);
  const goTrend = (a) => navigate(`/platform-admin/intelligence/trends${f.carry({ metric: a.metric, month: a.period })}`);
  const goBusiness = (a) => f.tenant && navigate(`/platform-admin/intelligence/business/${f.tenant}${f.carry({ month: a.period })}`);
  const items = data?.items || [];
  const unusual = items.filter(isAnomalyUnusual);
  const others = items.filter((i) => !isAnomalyUnusual(i));
  return (
    <div data-testid="in-anomalies-page">
      <Header title="Anomalies" subtitle="Statistically unusual movements vs the selected entity's OWN history. Descriptive — no forecasting, ranking or causal claims." right={<div className="flex flex-col items-end gap-2">{f.node}<ExploreExport f={f} /></div>} />
      <Breadcrumbs items={[{ label: "Intelligence", to: "/platform-admin/intelligence" }, { label: "Anomalies" }]} />
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <input data-testid="in-anom-period" value={period} onChange={(e) => f.setParam("period", e.target.value)} placeholder="Period YYYY-MM" className="w-36 rounded-lg border border-slate-800 bg-slate-900 px-3 py-2 text-sm" />
        <select data-testid="in-anom-vertical" value={avertical} onChange={(e) => f.setParam("avertical", e.target.value)} className="rounded-lg border border-slate-800 bg-slate-900 px-3 py-2 text-sm">
          <option value="">Core metrics</option><option value="veterinary">+ Veterinary</option><option value="boarding">+ Boarding</option><option value="training">+ Training</option><option value="grooming">+ Grooming</option>
        </select>
        <div className="flex overflow-hidden rounded-lg border border-slate-800">
          {ANOMALY_BASELINE_OPTS.map((r) => (
            <button key={r.v} data-testid={`in-anom-baseline-${r.v}`} onClick={() => f.setParam("abaseline", r.v)} className={`px-3 py-2 text-sm ${baseline === r.v ? "bg-indigo-500/20 text-indigo-200" : "text-slate-400 hover:bg-slate-800"}`}>{r.l}</button>
          ))}
        </div>
      </div>
      {data && <DataTrust trust={data.data_trust} coverage={items.some((i) => ["UNUSUAL", "NORMAL", "DATA_STALE"].includes(i.status)) ? "AVAILABLE" : "NO_DATA"} />}
      <div className="mt-2 text-xs text-slate-500" data-testid="in-anom-scope">Period {data?.period || period || "—"} · baseline {data?.baseline_months || baseline} months · {f.tenant ? "selected business" : f.city || f.state ? "selected geography" : "aggregate across matching businesses"}. HIGH = larger statistical deviation, not "bad".</div>
      {error ? <div className="mt-4"><ErrorState error={error} onRetry={reload} /></div>
        : loading || !data ? <Loading />
        : !items.length ? <div className="mt-4"><Empty label="No metrics to analyse" /></div>
        : (<>
          <h3 className="mt-6 font-semibold text-amber-200" data-testid="in-anom-unusual-heading">Unusual movements ({unusual.length})</h3>
          {unusual.length === 0 ? <div className="mt-2 text-sm text-slate-500" data-testid="in-anom-no-unusual">No metrics are outside their recent historical pattern for this period.</div>
            : <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{unusual.map((a) => <AnomalyCard key={a.metric} a={a} onTrend={goTrend} onBusiness={f.tenant ? goBusiness : undefined} f={f} />)}</div>}
          <h3 className="mt-8 font-semibold text-slate-300" data-testid="in-anom-other-heading">Other metrics</h3>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{others.map((a) => <AnomalyCard key={a.metric} a={a} onTrend={["UNUSUAL", "NORMAL", "DATA_STALE"].includes(a.status) ? goTrend : undefined} f={f} />)}</div>
        </>)}
    </div>
  );
}

/* ------------------------------------------------------------ 6H data quality + evidence */
const DQ_SEV_STYLE = {
  MATERIAL: "bg-rose-500/15 text-rose-200 border-rose-500/30",
  ATTENTION: "bg-amber-500/15 text-amber-200 border-amber-500/30",
  INFORMATIONAL: "bg-slate-700/40 text-slate-300 border-slate-700",
};
const DQ_STATUS_STYLE = {
  AVAILABLE: "text-emerald-300", INCOMPLETE: "text-amber-300", STALE: "text-amber-300",
  FAILED: "text-rose-300", NO_DATA: "text-slate-500", NOT_SUPPORTED: "text-slate-500",
};
const DQ_DATASET_LABEL = { customers: "Customers", pets: "Pets", bookings: "Bookings",
  services: "Services", inventory: "Inventory", veterinary: "Veterinary", boarding: "Boarding",
  training: "Training", finance: "Finance", geography: "Geography", rollups: "Rollups" };
const DQ_EXPLORER_DATASET = { customers: "customers", pets: "pets", bookings: "bookings",
  services: "services", inventory: "inventory", finance: "invoices", geography: "customers",
  veterinary: "vertical_metrics", boarding: "vertical_metrics", training: "vertical_metrics",
  rollups: "vertical_metrics" };

function EvidenceDrawer({ open, onClose, metric, label, tenant_id, branch_id, vertical, month }) {
  const navigate = useNavigate();
  const q = new URLSearchParams();
  q.set("metric", metric);
  if (month) q.set("month", month);
  if (tenant_id) q.set("tenant_id", tenant_id);
  if (branch_id) q.set("branch_id", branch_id);
  if (vertical) q.set("vertical", vertical);
  const key = q.toString();
  const { data, loading, error } = useApi(open ? `/platform/intel/evidence?${key}` : null, [open, key]);
  if (!open) return null;
  const money = data && data.unit === "INR";
  const fv = (v) => (v == null ? "—" : money ? fmtINR(v) : fmtNum(v));
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50" data-testid="in-evidence-drawer" onClick={onClose}>
      <div className="h-full w-full max-w-md overflow-y-auto border-l border-slate-800 bg-slate-950 p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg font-bold">Calculation · {label || metric}</h3>
          <button data-testid="in-evidence-close" onClick={onClose} className="rounded-lg border border-slate-700 px-2 py-1 text-sm hover:bg-slate-800">Close</button>
        </div>
        {error ? <div className="mt-4 text-sm text-rose-300">Couldn’t load evidence.</div>
          : loading || !data ? <Loading />
          : data.status === "NOT_SUPPORTED" ? <Empty label="Evidence not supported for this metric" />
          : data.status === "NO_DATA" ? <Empty label="No source data for this period" />
          : (<>
            <div className="mt-4"><div className="text-xs text-slate-400">Value · {data.period}</div><div className="font-display text-2xl font-bold">{fv(data.value)}</div></div>
            <div className="mt-4 rounded-lg border border-slate-800 bg-slate-900/50 p-3 text-sm" data-testid="in-evidence-calc">
              <div className="text-xs uppercase text-slate-500">Calculation</div>
              <div className="mt-1 text-slate-200">{data.lineage.calculation}</div>
              <div className="mt-2 text-xs text-slate-500">Original records: {data.lineage.source_datasets.join(", ")} · summary data {data.lineage.rollup} ({data.lineage.rollup_rows} rows)</div>
            </div>
            {data.components && (
              <div className="mt-3 rounded-lg border border-slate-800 bg-slate-900/50 p-3 text-sm" data-testid="in-evidence-components">
                <div className="text-xs uppercase text-slate-500">Components</div>
                {Object.entries(data.components).map(([k, v]) => (
                  <div key={k} className="mt-1 flex justify-between"><span className="text-slate-400">{k}</span><span className="text-slate-200">{fmtINR(v)}</span></div>
                ))}
                {data.contribution_margin_note && <div className="mt-2 text-[11px] text-amber-300">{data.contribution_margin_note}</div>}
              </div>
            )}
            {data.attribution && (
              <div className="mt-3 rounded-lg border border-slate-800 bg-slate-900/50 p-3 text-sm" data-testid="in-evidence-attribution">
                <div className="text-xs uppercase text-slate-500">Attribution</div>
                {Object.entries(data.attribution).map(([k, v]) => (
                  <div key={k} className="mt-1 flex justify-between"><span className="text-slate-400">{VERTICAL_LABELS_JS[k] || k}</span><span className="text-slate-200">{fmtINR(v)}</span></div>
                ))}
                <div className="mt-2 text-xs" data-testid="in-evidence-reconcile">Reconciliation: <span className={data.reconciles ? "text-emerald-300" : "text-rose-300"}>{data.reconciles ? "MATCHED" : "RECONCILIATION_EXCEPTION"}</span></div>
              </div>
            )}
            <div className="mt-3"><DataTrust trust={data.data_trust} coverage={data.status === "AVAILABLE" ? "AVAILABLE" : data.status} /></div>
            <button data-testid="in-evidence-explore" onClick={() => navigate(`/platform-admin/explorer?dataset=${DQ_EXPLORER_DATASET[vertical === "finance" ? "finance" : "finance"] || "payments"}${tenant_id ? `&tenant_id=${tenant_id}` : ""}`)} className="mt-4 inline-flex items-center gap-1.5 rounded-lg border border-slate-700 px-3 py-2 text-sm hover:bg-slate-800"><Database className="h-4 w-4" /> Open in Data Explorer</button>
          </>)}
      </div>
    </div>
  );
}
const VERTICAL_LABELS_JS = { veterinary: "Veterinary", grooming: "Grooming", boarding: "Boarding", training: "Training", pet_store: "Pet Store", other: "Other / Unclassified" };

function EvidenceButton({ metric, label, tenant_id, branch_id, vertical, month, testid }) {
  const [open, setOpen] = useState(false);
  return (<>
    <button data-testid={testid || `in-evidence-${metric}`} onClick={() => setOpen(true)} className="mt-1 inline-flex items-center gap-1 text-[11px] text-indigo-300 hover:text-indigo-200"><Info className="h-3 w-3" /> View calculation</button>
    <EvidenceDrawer open={open} onClose={() => setOpen(false)} metric={metric} label={label} tenant_id={tenant_id} branch_id={branch_id} vertical={vertical} month={month} />
  </>);
}

function DQImpact({ findings }) {
  const impacts = [];
  const f = (ds, it) => findings.find((x) => x.dataset === ds && x.issue_type === it && x.status === "INCOMPLETE");
  const sc = f("services", "missing_cost"); if (sc) impacts.push(`"Amount left after direct costs" may be incomplete — ${sc.affected_count} service(s) lack cost data.`);
  const pc = f("inventory", "missing_cost"); if (pc) impacts.push(`Product cost totals may be incomplete — ${pc.affected_count} product(s) lack cost data.`);
  const geo = f("geography", "geography_unavailable"); if (geo) impacts.push(`Geography analytics may be incomplete — ${geo.affected_count} customer record(s) lack a normalized state.`);
  const br = f("bookings", "invalid_branch"); if (br) impacts.push(`Branch analytics may be incomplete — ${br.affected_count} booking(s) lack a branch reference.`);
  if (!impacts.length) return null;
  return (
    <Card title="Intelligence impact" testid="in-dq-impact"><ul className="space-y-1.5 text-sm text-slate-300">
      {impacts.map((t, i) => <li key={i} className="flex items-start gap-2"><Info className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />{t}</li>)}
    </ul></Card>
  );
}

export function IntelDataQuality() {
  const f = useFilters();
  const navigate = useNavigate();
  const dataset = f.sp.get("dataset") || "";
  const q = new URLSearchParams();
  if (f.tenant) q.set("tenant_id", f.tenant);
  if (f.bt) q.set("business_type", f.bt);
  if (f.plan) q.set("plan", f.plan);
  if (dataset) q.set("dataset", dataset);
  const key = q.toString();
  const sum = useApi(`/platform/intel/data-quality/summary?${key}`, ["s" + key]);
  const det = useApi(`/platform/intel/data-quality/details?${key}`, ["d" + key]);
  const data = sum.data, det_ = det.data;
  const t = data?.totals;
  return (
    <div data-testid="in-dq-page">
      <Header title="Data completeness" subtitle="How complete and reliable the data behind these reports is. Not a score of the business." right={<div className="flex flex-col items-end gap-2">{f.node}<ExploreExport f={f} /></div>} />
      <Breadcrumbs items={[{ label: "Intelligence", to: "/platform-admin/intelligence" }, { label: "Data completeness" }]} />
      {data && <DataTrust trust={{ freshness: "FRESH", computation_version: data.calculation_version }} coverage="AVAILABLE" />}
      {sum.error ? <div className="mt-4"><ErrorState error={sum.error} onRetry={sum.reload} /></div>
        : sum.loading || !data ? <Loading />
        : (<>
          <div className="mt-5 grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <Stat testid="in-dq-datasets" label="Datasets checked" value={fmtNum(t.datasets_checked)} />
            <Stat testid="in-dq-checks" label="Total checks" value={fmtNum(t.total_checks)} />
            <Stat label="Available" value={fmtNum(t.available)} tone="text-emerald-300" />
            <Stat label="Incomplete" value={fmtNum(t.incomplete)} tone="text-amber-300" />
            <Stat label="Stale" value={fmtNum(t.stale)} tone="text-amber-300" />
            <Stat label="Failed / No data" value={`${fmtNum(t.failed)} / ${fmtNum(t.no_data)}`} />
          </div>
          <div className="mt-5 grid gap-5 lg:grid-cols-2">
            <Card title="By dataset" testid="in-dq-by-dataset">
              <div className="overflow-x-auto"><table className="w-full text-sm">
                <thead className="text-left text-xs uppercase text-slate-500"><tr>{["Dataset", "Checks", "Available", "Incomplete", "Affected", ""].map((h) => <th key={h} className="px-2 py-1.5 font-medium">{h}</th>)}</tr></thead>
                <tbody>{(data.by_dataset || []).map((d) => (
                  <tr key={d.dataset} data-testid={`in-dq-ds-${d.dataset}`} onClick={() => f.setParam("dataset", d.dataset === dataset ? "" : d.dataset)} className="cursor-pointer border-t border-slate-800 hover:bg-slate-800/40">
                    <td className="px-2 py-1.5 text-slate-200">{DQ_DATASET_LABEL[d.dataset] || d.dataset}</td>
                    <td className="px-2 py-1.5">{d.checks}</td><td className="px-2 py-1.5 text-emerald-300">{d.available}</td>
                    <td className="px-2 py-1.5 text-amber-300">{d.incomplete + d.stale + d.failed}</td>
                    <td className="px-2 py-1.5">{fmtNum(d.affected_records)}</td>
                    <td className="px-2 py-1.5 text-right text-indigo-300"><ChevronRight className="inline h-4 w-4" /></td>
                  </tr>))}</tbody>
              </table></div>
            </Card>
            {det_ && <DQImpact findings={det_.findings} />}
          </div>
          <h3 className="mt-6 font-semibold text-slate-200">Findings{dataset ? ` · ${DQ_DATASET_LABEL[dataset] || dataset}` : ""}</h3>
          {det.loading || !det_ ? <Loading /> : (
            <div className="mt-3 overflow-x-auto rounded-xl border border-slate-800">
              <table className="w-full text-sm"><thead className="bg-slate-900/70 text-left text-xs uppercase text-slate-500"><tr>{["Dataset", "Issue", "Field", "Affected", "Total", "%", "Severity", "Status", ""].map((h) => <th key={h} className="px-3 py-2.5 font-medium">{h}</th>)}</tr></thead>
                <tbody>{det_.findings.map((x, i) => (
                  <tr key={i} data-testid={`in-dq-finding-${x.dataset}-${x.issue_type}`} className="border-t border-slate-800">
                    <td className="px-3 py-2.5 text-slate-300">{DQ_DATASET_LABEL[x.dataset] || x.dataset}</td>
                    <td className="px-3 py-2.5 text-slate-200">{x.issue_type.replace(/_/g, " ")}</td>
                    <td className="px-3 py-2.5 text-slate-400">{x.field}</td>
                    <td className="px-3 py-2.5">{fmtNum(x.affected_count)}</td><td className="px-3 py-2.5">{fmtNum(x.total_count)}</td>
                    <td className="px-3 py-2.5">{x.affected_percentage}%</td>
                    <td className="px-3 py-2.5"><span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${DQ_SEV_STYLE[x.severity]}`}>{x.severity}</span></td>
                    <td className={`px-3 py-2.5 text-xs ${DQ_STATUS_STYLE[x.status] || ""}`}>{x.status}</td>
                    <td className="px-3 py-2.5 text-right"><button data-testid={`in-dq-explore-${x.dataset}-${x.issue_type}`} onClick={() => navigate(`/platform-admin/explorer?dataset=${DQ_EXPLORER_DATASET[x.dataset] || "businesses"}${f.tenant ? `&tenant_id=${f.tenant}` : ""}`)} className="inline-flex items-center gap-1 rounded-lg border border-slate-700 px-2 py-1 text-xs hover:bg-slate-800"><Database className="h-3.5 w-3.5" /> Explorer</button></td>
                  </tr>))}</tbody>
              </table>
            </div>
          )}
          {f.tenant && det_ && <DQTrend tenant_id={f.tenant} />}
        </>)}
    </div>
  );
}

function DQTrend({ tenant_id }) {
  const { data } = useApi(`/platform/intel/data-quality/trend?tenant_id=${tenant_id}&months=12`, [tenant_id]);
  if (!data || !Object.keys(data.series || {}).length) return null;
  const keys = Object.keys(data.series).filter((k) => data.series[k].length > 1).slice(0, 4);
  if (!keys.length) return null;
  return (
    <div className="mt-6" data-testid="in-dq-trend">
      <h3 className="font-semibold text-slate-200">Data quality trend (monthly)</h3>
      <div className="mt-3 grid gap-4 sm:grid-cols-2">
        {keys.map((k) => (
          <Card key={k} title={k.replace(/_/g, " ")}><Spark series={data.series[k].map((p) => ({ month: p.month, value: p.affected_percentage }))} /></Card>
        ))}
      </div>
      <div className="mt-2 text-xs text-slate-500">Descriptive movement only — not a quality score.</div>
    </div>
  );
}

const IN_NAV = [
  { to: "/platform-admin/intelligence", end: true, icon: LayoutGrid, label: "Overview" },
  { to: "/platform-admin/intelligence/pets", icon: PawPrint, label: "Pet Population" },
  { to: "/platform-admin/intelligence/customers", icon: Users, label: "Customers" },
  { to: "/platform-admin/intelligence/geography", icon: MapPin, label: "Geography" },
  { to: "/platform-admin/intelligence/business", icon: Building2, label: "Business" },
  { to: "/platform-admin/intelligence/services", icon: Sparkles, label: "Services" },
  { to: "/platform-admin/intelligence/inventory", icon: Boxes, label: "Inventory" },
  { to: "/platform-admin/intelligence/grooming", icon: Scissors, label: "Grooming" },
  { to: "/platform-admin/intelligence/veterinary", icon: Stethoscope, label: "Veterinary" },
  { to: "/platform-admin/intelligence/boarding", icon: BedDouble, label: "Boarding" },
  { to: "/platform-admin/intelligence/training", icon: GraduationCap, label: "Training" },
  { to: "/platform-admin/intelligence/finance", icon: Wallet, label: "Finance" },
  { to: "/platform-admin/intelligence/trends", icon: Activity, label: "Trends" },
  { to: "/platform-admin/intelligence/compare", icon: Users, label: "Comparative", plain: "Compare" },
  { to: "/platform-admin/intelligence/anomalies", icon: Zap, label: "Anomalies", plain: "Unusual changes" },
  { to: "/platform-admin/intelligence/data-quality", icon: Database, label: "Data Quality", plain: "Data completeness" },
  { to: "/platform-admin/intelligence/seasonality", icon: TrendingUp, label: "Seasonality", plain: "Seasonal demand" },
];

function IntelGate({ children }) {
  const { data, loading, error, reload } = useApi("/platform/intel/availability");
  if (loading) return <Loading />;
  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (!data.enabled) return (
    <div data-testid="in-disabled" className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-6">
      <div className="font-medium text-amber-200">Pawkt Intelligence is switched off in this environment</div>
      <p className="mt-1 text-sm text-slate-400">The setting INTEL_ENABLED is "false" here, so these reports are not served. This is a configuration choice, not a data error. The Command Center is not affected.</p>
    </div>);
  return children;
}

export function IntelligenceShell() {
  const navigate = useNavigate();
  return (
    <div data-testid="in-shell">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button onClick={() => navigate("/platform-admin")} className="flex items-center gap-1 text-sm text-slate-400 hover:text-slate-100"><ArrowLeft className="h-4 w-4" /> Platform</button>
      </div>
      <div className="mt-2 flex items-center gap-2">
        <div className="h-9 w-9 rounded-lg bg-indigo-500/15 grid place-items-center"><Brain className="h-5 w-5 text-indigo-400" /></div>
        <div><div className="font-display text-xl font-extrabold">Pawkt Intelligence</div>
          <div className="text-xs text-slate-500">Read-only cross-platform intelligence · Preview</div></div>
      </div>
      <nav className="mt-4 -mx-4 flex gap-1.5 overflow-x-auto border-b border-slate-800 px-4 pb-3 sm:mx-0 sm:flex-wrap sm:px-0">
        {IN_NAV.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} data-testid={`in-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
            className={({ isActive }) => `flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium ${isActive ? "bg-indigo-500/10 text-indigo-300" : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/60"}`}>
            <n.icon className="h-4 w-4" /> {n.plain || n.label}
          </NavLink>
        ))}
      </nav>
      <div className="mt-6"><IntelGate><Outlet /></IntelGate></div>
    </div>
  );
}

/* ------------------------------------------------------------ 1. overview */
export function IntelOverview() {
  const f = useFilters();
  const navigate = useNavigate();
  const { data, loading, error, reload } = useApi(`/platform/intel/dash/overview?${f.qs}&month=${enc(f.month)}&comparison=${enc(f.comparison)}`, [f.qs, f.month, f.comparison]);
  const goVertical = (v) => {
    const map = { veterinary: "veterinary", grooming: "grooming", boarding: "boarding", training: "training" };
    if (map[v]) navigate(`/platform-admin/intelligence/${map[v]}${f.carry()}`);
  };
  return (
    <div data-testid="in-overview">
      <Header title="Intelligence Overview" subtitle="Aggregate view across every Pawkt business." right={f.node} />
      {error ? <div className="mt-4"><ErrorState error={error} onRetry={reload} /></div>
        : loading || !data ? <Loading />
        : !data.data_available ? <div className="mt-4"><Empty hint="No businesses match this filter yet." /></div>
        : (<>
          <div className="mt-5 grid gap-3 sm:grid-cols-3 lg:grid-cols-4">
            <Stat testid="in-ov-gmv" label="Money received (summary data, before refunds)" help="money_received" value={fmtINR(data.totals.gmv)} sub={`Kept after refunds ${fmtINR(data.totals.net_gmv)}`} />
            <Stat testid="in-ov-revenue" label="Money received (live payment records)" help="money_received_live" value={fmtINR(data.revenue_mix.total_revenue)} />
            <Stat label="Refunds" value={fmtINR(data.totals.refunds)} tone="text-amber-300" />
            <Stat testid="in-ov-cm" label="Amount left after direct costs" help="margin" value={fmtINR(data.totals.contribution_margin)} sub="Not profit — running costs not deducted" tone="text-indigo-300" />
            <Stat label="Pawkt subscription income / month (est.)" help="mrr" value={fmtINR(data.pawkt_saas.derived_mrr)} sub={`Per year (est.) ${fmtINR(data.pawkt_saas.arr_run_rate)}`} />
            <Stat label="Businesses" value={fmtNum(data.totals.businesses)} sub={`${data.totals.active_businesses} active · ${data.new_this_month.businesses} new`} />
            <Stat label="Customers" value={fmtNum(data.totals.customers)} help="mom" sub={`${pct(data.growth_mom.new_customers)} vs previous month`} />
            <Stat label="Pets" value={fmtNum(data.totals.pets)} help="mom" sub={`${pct(data.growth_mom.new_pets)} vs previous month`} />
            <Stat label="Bookings" value={fmtNum(data.totals.bookings)} help="mom" sub={`${pct(data.growth_mom.bookings)} vs previous month`} />
            <Stat label="Branches" value={fmtNum(data.totals.branches)} />
          </div>
          <div className="mt-3 rounded-lg border border-slate-800 bg-slate-900/40 px-4 py-2 text-xs text-slate-400" data-testid="in-ov-note">
            {data.pawkt_saas.note} · {data.contribution_margin_completeness.note}
            {data.contribution_margin_completeness.ratio !== null && ` (cost data complete for ${Math.round(data.contribution_margin_completeness.ratio * 100)}% of business-months)`}
          </div>
          <div className="mt-3 rounded-lg border border-slate-800 bg-slate-900/40 px-4 py-2 text-xs text-slate-400" data-testid="in-ov-money-explain">
            <span className="font-semibold text-slate-300">Why the money figures differ: </span>
            Both "Money received" figures are all-time, include GST and are before refunds. The live figure reads every payment record ({fmtINR(data.revenue_mix.total_revenue)}); the summary figure ({fmtINR(data.totals.gmv)}) is refreshed in the background and can only count payments it can place in a month, so it leaves out payments with no recorded date or from businesses that no longer exist. A small gap either way can also mean the summary has not yet caught up with recently added or deleted payments{Math.abs(data.revenue_mix.total_revenue - data.totals.gmv) >= 1 ? ` (difference here: ${fmtINR(Math.abs(data.revenue_mix.total_revenue - data.totals.gmv))})` : ""}. These totals are all-time; the month filter applies to the period comparison below. The Command Center's "Money received after refunds" covers only its selected dates (default last 90 days) and subtracts refunds, so it is lower.
          </div>
          {data.attribution && (
            <div className="mt-4 rounded-xl border border-slate-800 bg-slate-900/50 p-4" data-testid="in-attribution">
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-semibold">Data attribution</h3>
                <div className="flex gap-2">
                  <button data-testid="in-ov-explore-trends" onClick={() => navigate(`/platform-admin/intelligence/trends${f.carry()}`)} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 px-3 py-1.5 text-xs text-slate-200 hover:bg-slate-800"><Activity className="h-3.5 w-3.5" /> Explore trends</button>
                  <button data-testid="in-attribution-explore" onClick={() => navigate("/platform-admin/explorer?dataset=payments")} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 px-3 py-1.5 text-xs text-slate-200 hover:bg-slate-800"><Database className="h-3.5 w-3.5" /> Explore attribution</button>
                </div>
              </div>
              {data.attribution.status !== "AVAILABLE" ? (
                <div className="mt-2 text-sm text-slate-400">Not available</div>
              ) : (<>
                <div className="mt-2 text-sm text-slate-300" data-testid="in-attribution-summary">
                  {data.attribution.other === 0
                    ? "100% of captured revenue is attributed to recognized verticals."
                    : `${data.attribution.attributed_pct}% attributed to recognized verticals · ${data.attribution.other_pct}% Other / Unclassified`}
                </div>
                <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-800">
                  <div className="h-full rounded-full bg-indigo-500" style={{ width: `${data.attribution.attributed_pct || 0}%` }} />
                </div>
                <div className="mt-2 grid grid-cols-3 gap-3 text-xs">
                  <div><div className="text-slate-500">Total captured</div><div className="font-semibold text-slate-200">{fmtINR(data.attribution.total)}</div></div>
                  <div><div className="text-slate-500">Attributed</div><div className="font-semibold text-slate-200">{fmtINR(data.attribution.attributed)}</div></div>
                  <div><div className="text-slate-500">Other / Unclassified</div><div className="font-semibold text-slate-200">{fmtINR(data.attribution.other)}</div></div>
                </div>
                <div className="mt-2 text-[11px] text-slate-500">{data.attribution.note}</div>
              </>)}
            </div>
          )}
          {data.comparisons && (
            <div className="mt-5" data-testid="in-ov-comparisons">
              <h3 className="font-semibold text-slate-200">Period comparison · {data.comparisons.month} vs {data.comparisons.comparison_period} ({CMP_TXT[data.comparisons.comparison] || data.comparisons.comparison})</h3>
              <div className="mt-2 grid gap-3 sm:grid-cols-3 lg:grid-cols-4">
                {data.comparisons.items.map((c) => (
                  <PeriodComparison key={c.metric} cmp={c} label={PLAIN_METRIC[c.metric] || c.label}
                    money={["revenue", "net_revenue", "contribution_margin", "refunds", "gmv"].includes(c.metric)} />
                ))}
              </div>
            </div>
          )}
          <UnusualMovements testid="in-ov-unusual" />
          <div className="mt-5 grid gap-5 lg:grid-cols-2">
            <Card title="Money received by service type" testid="in-ov-revenue-mix" right={<span className="text-xs text-slate-500">totals match the payment records</span>}>
              <RevenueDonut mix={data.revenue_mix.mix} onSlice={goVertical} />
              <div className="mt-2 text-xs text-slate-500">{data.revenue_mix.note}</div>
            </Card>
            <Card title="Business vertical distribution" testid="in-ov-vertical-dist" right={<span className="text-xs text-slate-500">non-exclusive</span>}>
              <div className="space-y-1.5">
                {data.vertical_distribution.map((v) => (
                  <div key={v.vertical} data-testid={`in-ov-dist-${v.vertical}`} onClick={() => goVertical(v.vertical)}
                    className={`flex items-center justify-between rounded-md px-2 py-1.5 text-sm ${["veterinary", "grooming", "boarding", "training"].includes(v.vertical) ? "cursor-pointer hover:bg-slate-800/50" : ""}`}>
                    <span className="flex items-center gap-2 text-slate-300"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: MIX_COLORS[v.vertical] }} />{v.label}</span>
                    <span className="font-medium text-slate-200">{fmtNum(v.businesses)}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
          <div className="mt-5 grid gap-5 lg:grid-cols-2">
            <Card title="Money received each month" testid="in-ov-gmv-trend"><Spark series={data.monthly_trend.map((m) => ({ month: m.month, value: m.gmv }))} money /></Card>
            <Card title="Monthly new customers"><Spark series={data.monthly_trend.map((m) => ({ month: m.month, value: m.new_customers }))} /></Card>
          </div>
        </>)}
    </div>
  );
}

/* ------------------------------------------------------------ 2. pets */
export function IntelPets() {
  const f = useFilters();
  const { data, loading, error, reload } = useApi(`/platform/intel/dash/pets?${f.qs}`, [f.qs]);
  const c = data?.completeness;
  return (
    <div data-testid="in-pets">
      <Header title="Pet Population" subtitle="Species, breeds, age and geography across the platform." right={f.node} />
      {error ? <div className="mt-4"><ErrorState error={error} onRetry={reload} /></div>
        : loading || !data ? <Loading />
        : !data.data_available ? <div className="mt-4"><Empty hint="No pets recorded for this filter." /></div>
        : (<>
          {c && <div className="mt-4 grid gap-3 sm:grid-cols-4">
            <Stat testid="in-pets-total" label="Total pets" value={fmtNum(data.total_pets)} />
            <Stat label="Missing DOB" value={fmtNum(c.missing_dob)} sub={`${Math.round((c.missing_dob / (c.total || 1)) * 100)}% incomplete`} tone="text-amber-300" />
            <Stat label="Missing breed" value={fmtNum(c.missing_breed)} tone="text-amber-300" />
            <Stat label="Missing gender" value={fmtNum(c.missing_gender)} tone="text-amber-300" />
          </div>}
          <div className="mt-5 grid gap-5 lg:grid-cols-2">
            <Card title="Species"><BarList items={data.species} labelKey="species" valueKey="count" /></Card>
            <Card title="Top breeds"><BarList items={data.top_breeds} labelKey="breed" valueKey="count" /></Card>
            <Card title="Gender" right={<span className="text-xs text-slate-500">where recorded</span>}><BarList items={data.gender} labelKey="gender" valueKey="count" /></Card>
            <Card title="Age distribution" right={<span className="text-xs text-slate-500">DOB required</span>}>{data.age_distribution.length ? <BarList items={data.age_distribution} labelKey="key" valueKey="count" /> : <Empty label="No DOB data" hint="Ages appear once pets have a date of birth." />}</Card>
            <Card title="Pets by state"><BarList items={data.by_state} labelKey="state" valueKey="count" /></Card>
            <Card title="Monthly new pets"><Spark series={data.growth} /></Card>
          </div>
        </>)}
    </div>
  );
}

/* ------------------------------------------------------------ 3. customers */
export function IntelCustomers() {
  const f = useFilters();
  const { data, loading, error, reload } = useApi(`/platform/intel/dash/customers?${f.qs}`, [f.qs]);
  const l = data?.lifecycle;
  return (
    <div data-testid="in-customers">
      <Header title="Customer Intelligence" subtitle="Lifecycle, spend and geography of pet parents." right={f.node} />
      {error ? <div className="mt-4"><ErrorState error={error} onRetry={reload} /></div>
        : loading || !data ? <Loading />
        : !data.data_available ? <div className="mt-4"><Empty hint="No customers recorded for this filter." /></div>
        : (<>
          <div className="mt-4 grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <Stat testid="in-cust-total" label="Total" value={fmtNum(data.total_customers)} />
            <Stat label="Active" value={fmtNum(l.active)} sub="paid ≤90d" tone="text-emerald-300" />
            <Stat label="New" value={fmtNum(l.new)} />
            <Stat label="Returning" value={fmtNum(l.returning)} />
            <Stat label="Dormant" value={fmtNum(l.dormant)} sub="no paid ≥90d" tone="text-amber-300" />
            <Stat label="Prospect" value={fmtNum(l.prospect)} sub="no purchase" />
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            <Stat testid="in-cust-ltv" label="Average lifetime spend" help="ltv" value={fmtINR(data.ltv.avg)} sub={`${fmtNum(data.ltv.paying_customers)} paying`} />
            <Stat label="Avg transaction value" value={fmtINR(data.avg_transaction_value)} />
            <Stat label="Avg purchase frequency" value={data.avg_purchase_frequency} sub="per paying customer" />
          </div>
          <div className="mt-3 rounded-lg border border-slate-800 bg-slate-900/40 px-4 py-2 text-xs text-slate-400">{data.note}</div>
          <div className="mt-5 grid gap-5 lg:grid-cols-2">
            <Card title="Spend by state"><BarList items={data.spend_by_state} labelKey="state" valueKey="spend" money /></Card>
            <Card title="Spend by customer type"><BarList items={data.spend_by_customer_type} labelKey="type" valueKey="spend" money /></Card>
            <Card title="Monthly new customers"><Spark series={data.growth} /></Card>
          </div>
        </>)}
    </div>
  );
}

/* ------------------------------------------------------------ 4. geography (hierarchical) */
export function IntelGeography() {
  const f = useFilters();
  const navigate = useNavigate();
  const { state, city } = f;
  const level = city ? "city" : state ? "state" : "india";
  const path = city
    ? `/platform/intel/dash/geography/city?state=${enc(state)}&city=${enc(city)}&${f.qs}`
    : `/platform/intel/dash/geography?${f.qs}&state=${enc(state)}`;
  const { data, loading, error, reload } = useApi(path, [path]);
  const crumbs = [{ label: "India", to: `/platform-admin/intelligence/geography${f.carry({ state: "", city: "" })}` }];
  if (state) crumbs.push({ label: state, to: `/platform-admin/intelligence/geography${f.carry({ state, city: "" })}` });
  if (city) crumbs.push({ label: city });
  const cols = level === "city"
    ? ["Business", "Type", "Customers", "Pets", "Bookings", "Money received", "Received before refunds", "Left after direct costs"]
    : [level === "state" ? "City" : "State", "Businesses", "Customers", "Pets", "Money received", "Bookings", "Avg spend"];
  const rowClick = (r) => {
    if (level === "india" && r.state !== "UNKNOWN") navigate(`/platform-admin/intelligence/geography${f.carry({ state: r.state, city: "" })}`);
    else if (level === "state" && r.city !== "UNKNOWN") navigate(`/platform-admin/intelligence/geography${f.carry({ state, city: r.city })}`);
    else if (level === "city") navigate(`/platform-admin/intelligence/business/${r.tenant_id}${f.carry()}`);
  };
  return (
    <div data-testid="in-geography">
      <Header title="Geographic Intelligence" subtitle="India → State → City → Business → Branch. Based on business locations; exact addresses are never shown." right={f.node} />
      <Breadcrumbs items={crumbs} />
      <div className="mt-3"><ExploreExport f={f} exploreDataset={level === "city" ? "businesses" : undefined} /></div>
      {error ? <div className="mt-4"><ErrorState error={error} onRetry={reload} /></div>
        : loading || !data ? <Loading />
        : !data.data_available ? <div className="mt-4"><Empty label="Geography coverage is partial" hint="Geography appears once customers have a mappable city." /></div>
        : (
          <div className="mt-4 overflow-x-auto rounded-xl border border-slate-800">
            <table className="w-full text-sm">
              <thead className="bg-slate-900/70 text-left text-xs uppercase text-slate-500"><tr>{cols.map((h) => <th key={h} className="px-3 py-2.5 font-medium">{h}</th>)}</tr></thead>
              <tbody>
                {data.rows.map((r, i) => (
                  <tr key={i} data-testid={`in-geo-row-${i}`} onClick={() => rowClick(r)}
                    className={`border-t border-slate-800 ${(level === "india" && r.state !== "UNKNOWN") || (level === "state" && r.city !== "UNKNOWN") || level === "city" ? "cursor-pointer hover:bg-slate-800/40" : ""}`}>
                    {level === "city" ? (<>
                      <td className="px-3 py-2.5 font-medium text-slate-100">{r.name || "—"}</td>
                      <td className="px-3 py-2.5 text-xs text-slate-400">{(r.business_types || []).join(", ") || "—"}</td>
                      <td className="px-3 py-2.5">{fmtNum(r.customers)}</td><td className="px-3 py-2.5">{fmtNum(r.pets)}</td>
                      <td className="px-3 py-2.5">{fmtNum(r.bookings)}</td><td className="px-3 py-2.5">{fmtINR(r.revenue)}</td>
                      <td className="px-3 py-2.5">{fmtINR(r.gmv)}</td><td className="px-3 py-2.5">{fmtINR(r.contribution_margin)}</td>
                    </>) : (<>
                      <td className="px-3 py-2.5 font-medium text-slate-100">{r.state || r.city}</td>
                      <td className="px-3 py-2.5">{fmtNum(r.businesses)}</td><td className="px-3 py-2.5">{fmtNum(r.customers)}</td>
                      <td className="px-3 py-2.5">{fmtNum(r.pets)}</td><td className="px-3 py-2.5">{fmtINR(r.gmv)}</td>
                      <td className="px-3 py-2.5">{fmtNum(r.bookings)}</td><td className="px-3 py-2.5">{fmtINR(r.avg_customer_spend)}</td>
                    </>)}
                  </tr>
                ))}
                {!data.rows.length && <tr><td colSpan={cols.length} className="px-3 py-6 text-center text-slate-500">No data</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      {data?.note && <div className="mt-3 text-xs text-slate-500">{data.note}</div>}
    </div>
  );
}

/* ------------------------------------------------------------ 5. business */
export function IntelBusiness() {
  const f = useFilters();
  const navigate = useNavigate();
  const [sort, setSort] = useState("gmv");
  const path = `/platform/intel/dash/business?${f.qs}&sort=${sort}&limit=100`;
  const { data, loading, error, reload } = useApi(path, [path]);
  return (
    <div data-testid="in-business">
      <Header title="Business Performance" subtitle="Compare businesses on explicit metrics — no opaque rankings." right={f.node} />
      <div className="mt-3 flex items-center gap-2 text-sm">
        <span className="text-slate-400">Sort by</span>
        <select data-testid="in-biz-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="rounded-lg border border-slate-800 bg-slate-900 px-3 py-1.5 text-sm">
          {(data?.sortable_metrics || ["gmv", "bookings", "contribution_margin", "customers", "pets", "new_customers", "new_pets"]).map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>
      {error ? <div className="mt-4"><ErrorState error={error} onRetry={reload} /></div>
        : loading || !data ? <Loading />
        : !data.data_available ? <div className="mt-4"><Empty hint="No business metrics for this filter." /></div>
        : (
          <div className="mt-4 overflow-x-auto rounded-xl border border-slate-800">
            <table className="w-full text-sm">
              <thead className="bg-slate-900/70 text-left text-xs uppercase text-slate-500">
                <tr>{["Business", "Plan", "Money received", "Left after direct costs", "Bookings", "Customers", "Pets"].map((h) => <th key={h} className="px-3 py-2.5 font-medium">{h}</th>)}</tr>
              </thead>
              <tbody>
                {data.items.map((r) => (
                  <tr key={r.tenant_id} data-testid={`in-biz-row-${r.tenant_id}`} onClick={() => navigate(`/platform-admin/intelligence/business/${r.tenant_id}${f.carry()}`)} className="cursor-pointer border-t border-slate-800 hover:bg-slate-800/40">
                    <td className="px-3 py-2.5"><div className="font-medium text-slate-100">{r.name}</div><div className="text-xs text-slate-500">{r.business_types.join(", ") || "—"} · <span className={r.status === "suspended" ? "text-rose-400" : "text-emerald-400"}>{r.status}</span></div></td>
                    <td className="px-3 py-2.5">{r.plan}</td>
                    <td className="px-3 py-2.5">{fmtINR(r.gmv)}</td>
                    <td className="px-3 py-2.5"><div className="flex items-center gap-2">{fmtINR(r.contribution_margin)}<CompletenessBadge complete={r.contribution_margin_complete} /></div></td>
                    <td className="px-3 py-2.5">{fmtNum(r.bookings)}</td><td className="px-3 py-2.5">{fmtNum(r.customers)}</td><td className="px-3 py-2.5">{fmtNum(r.pets)}</td>
                  </tr>
                ))}
                {!data.items.length && <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-500">No businesses</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      {data && <div className="mt-3 text-xs text-slate-500">{data.note}</div>}
    </div>
  );
}

/* ------------------------------------------------------------ 6. services */
export function IntelServices() {
  const f = useFilters();
  const { data, loading, error, reload } = useApi(`/platform/intel/dash/services?${f.qs}`, [f.qs]);
  return (
    <div data-testid="in-services">
      <Header title="Services Intelligence" subtitle="Demand, price and reliability of services." right={f.node} />
      {error ? <div className="mt-4"><ErrorState error={error} onRetry={reload} /></div>
        : loading || !data ? <Loading />
        : !data.data_available ? <div className="mt-4"><Empty hint="No bookings recorded for this filter." /></div>
        : (<>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <Stat testid="in-svc-bookings" label="Total bookings" value={fmtNum(data.total_bookings)} />
            <Stat label="Avg service price" value={fmtINR(data.avg_service_price)} />
            <Stat label="Repeat rate" value={`${data.repeat_rate}%`} sub="same service, repeat customer" />
          </div>
          <div className="mt-5 grid gap-5 lg:grid-cols-2">
            <Card title="Service categories"><BarList items={data.categories} labelKey="category" valueKey="count" /></Card>
            <Card title="Most booked services"><BarList items={data.popular_services} labelKey="service" valueKey="bookings" /></Card>
          </div>
          <Card title="Cancellation & no-show rates" testid="in-svc-rates">
            <div className="overflow-x-auto"><table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-slate-500"><tr>{["Service", "Bookings", "Completed", "Cancelled", "No-show", "Cancel %", "No-show %"].map((h) => <th key={h} className="px-2 py-1.5 font-medium">{h}</th>)}</tr></thead>
              <tbody>{data.popular_services.map((s, i) => (
                <tr key={i} className="border-t border-slate-800"><td className="px-2 py-1.5 text-slate-200">{s.service}</td><td className="px-2 py-1.5">{s.bookings}</td><td className="px-2 py-1.5">{s.completed}</td><td className="px-2 py-1.5">{s.cancelled}</td><td className="px-2 py-1.5">{s.no_show}</td><td className="px-2 py-1.5 text-amber-300">{s.cancellation_rate}%</td><td className="px-2 py-1.5 text-amber-300">{s.no_show_rate}%</td></tr>))}</tbody>
            </table></div>
          </Card>
          <div className="mt-3 text-xs text-slate-500">{data.note}</div>
        </>)}
    </div>
  );
}

/* ------------------------------------------------------------ 7. inventory */
export function IntelInventory() {
  const f = useFilters();
  const { data, loading, error, reload } = useApi(`/platform/intel/dash/inventory?${f.qs}`, [f.qs]);
  return (
    <div data-testid="in-inventory">
      <Header title="Inventory Intelligence" subtitle="Movement, dead stock and stock health." right={f.node} />
      {error ? <div className="mt-4"><ErrorState error={error} onRetry={reload} /></div>
        : loading || !data ? <Loading />
        : !data.data_available ? <div className="mt-4"><Empty hint="No products recorded for this filter." /></div>
        : (<>
          <div className="mt-4 grid gap-3 sm:grid-cols-4">
            <Stat testid="in-inv-products" label="Products" value={fmtNum(data.total_products)} />
            <Stat label="Stock value" value={fmtINR(data.inventory_cost_value)} />
            <Stat label="Stock-outs" value={fmtNum(data.stock_outs)} tone={data.stock_outs ? "text-rose-300" : ""} />
            <Stat label="Dead stock" value={fmtNum(data.dead_stock_count)} sub="no recorded sales" tone={data.dead_stock_count ? "text-amber-300" : ""} />
          </div>
          <div className="mt-5 grid gap-5 lg:grid-cols-2">
            <Card title="Fastest moving">{data.fastest_moving.length ? <BarList items={data.fastest_moving} labelKey="name" valueKey="consumed" /> : <Empty label="No sales movement yet" />}</Card>
            <Card title="Monthly consumption"><Spark series={data.monthly_consumption} /></Card>
            <Card title="Low / out of stock" testid="in-inv-low">{data.low_stock.length ? (
              <div className="space-y-1.5 text-sm">{data.low_stock.map((p, i) => (
                <div key={i} className="flex justify-between"><span className="text-slate-300">{p.name}</span><span className={p.stock <= 0 ? "text-rose-400" : "text-amber-300"}>{p.stock} / min {p.min_stock}</span></div>))}</div>
            ) : <div className="text-sm text-slate-500">All products above minimum stock</div>}</Card>
            <Card title="Dead stock">{data.dead_stock.length ? (
              <div className="flex flex-wrap gap-1.5">{data.dead_stock.map((p, i) => <span key={i} className="rounded-md bg-slate-800 px-2 py-1 text-xs text-slate-300">{p.name}</span>)}</div>
            ) : <div className="text-sm text-slate-500">No dead stock</div>}</Card>
          </div>
          <div className="mt-3 text-xs text-slate-500">{data.note}</div>
        </>)}
    </div>
  );
}

/* ------------------------------------------------------------ 8. grooming */
export function IntelGrooming() {
  const f = useFilters();
  const { data, loading, error, reload } = useApi(`/platform/intel/dash/grooming?${f.qs}`, [f.qs]);
  return (
    <div data-testid="in-grooming">
      <Header title="Grooming Intelligence" subtitle="Grooming operations across the platform." right={f.node} />
      {error ? <div className="mt-4"><ErrorState error={error} onRetry={reload} /></div>
        : loading || !data ? <Loading />
        : !data.data_available ? <div className="mt-4"><Empty label="No grooming activity" hint="This appears once grooming tickets or bookings exist." /></div>
        : (<>
          <div className="mt-4 grid gap-3 sm:grid-cols-4">
            <Stat testid="in-groom-bookings" label="Grooming bookings" value={fmtNum(data.grooming_bookings)} />
            <Stat label="Tickets" value={fmtNum(data.total_tickets)} />
            <Stat label="Incidents" value={fmtNum(data.incidents)} tone={data.incidents ? "text-amber-300" : ""} />
            <Stat label="Avg interval" value={data.avg_grooming_interval_days ? `${data.avg_grooming_interval_days} days` : "—"} />
          </div>
          <div className="mt-5 grid gap-5 lg:grid-cols-2">
            <Card title="Ticket status"><BarList items={data.ticket_status} labelKey="status" valueKey="count" /></Card>
            <Card title="Monthly grooming bookings"><Spark series={data.monthly_trend} /></Card>
            <Card title="Staff productivity (commission)" testid="in-groom-staff">{data.staff_productivity.length ? <BarList items={data.staff_productivity} labelKey="staff" valueKey="commission" money /> : <Empty label="No staff commission recorded" />}</Card>
            <Card title="Add-ons"><div className="font-display text-2xl font-bold">{fmtNum(data.addons)}</div><div className="text-xs text-slate-500">add-on requests recorded</div></Card>
          </div>
          <div className="mt-3 text-xs text-slate-500">{data.note}</div>
        </>)}
    </div>
  );
}

/* ------------------------------------------------------------ 9. seasonality */
const SEASON_LABELS = { gmv: "Money received", bookings: "Bookings", new_customers: "New customers", new_pets: "New pets", invoice_total: "Invoiced", inventory_consumed: "Inventory consumed", contribution_margin: "Contribution margin" };
export function IntelSeasonality() {
  const f = useFilters();
  const { data, loading, error, reload } = useApi(`/platform/intel/dash/seasonality?${f.qs}`, [f.qs]);
  return (
    <div data-testid="in-seasonality">
      <Header title="Seasonal demand & trends" subtitle="Monthly patterns compared with the previous month and the same month last year. Shows patterns only, not causes." right={f.node} />
      {error ? <div className="mt-4"><ErrorState error={error} onRetry={reload} /></div>
        : loading || !data ? <Loading />
        : !data.data_available ? <div className="mt-4"><Empty hint="Trends appear once monthly data exists." /></div>
        : data.insufficient_history ? <div className="mt-4"><Empty label="Insufficient history" hint="At least two months of data are needed for trends." /></div>
        : (<>
          <div className="mt-5 grid gap-5 lg:grid-cols-2">
            {Object.entries(data.metrics).map(([k, v]) => (
              <Card key={k} title={SEASON_LABELS[k] || k} testid={`in-season-${k}`}
                right={<span className="flex items-center gap-1 text-xs text-slate-400">vs last month {pct(v.mom)} · vs last year {pct(v.yoy)}<InfoTip k="yoy" /></span>}>
                <Spark series={v.series} money={["gmv", "invoice_total", "contribution_margin"].includes(k)} />
                <div className="mt-1 text-xs text-slate-500">Baseline avg: {["gmv", "invoice_total", "contribution_margin"].includes(k) ? fmtINR(v.baseline) : fmtNum(v.baseline)}</div>
              </Card>
            ))}
          </div>
          <div className="mt-3 text-xs text-slate-500">{data.note}</div>
        </>)}
    </div>
  );
}

/* ------------------------------------------------------------ 6G-2/6G-3 data-trust + vertical dashboards */
const COV_TXT = { AVAILABLE: "Available", NO_DATA: "No data", PARTIAL: "Partial" };
const REC_TXT = { MATCHED: "Matches original records", SCOPED: "Filtered view (not checked)", MISMATCH: "Does not match — check data" };
function DataTrust({ trust, coverage, profitStatus, reconciliation }) {
  if (!trust) return null;
  const fresh = trust.freshness === "FRESH";
  const covTone = coverage === "AVAILABLE" ? "text-emerald-300" : coverage === "NO_DATA" ? "text-slate-400" : "text-amber-300";
  return (
    <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-1 rounded-lg border border-slate-800 bg-slate-900/40 px-4 py-2 text-xs" data-testid="in-data-trust">
      <span className="flex items-center gap-1.5 text-slate-400">Summary data:
        <span className={fresh ? "text-emerald-300" : "text-amber-300"} data-testid="in-freshness">
          {fresh ? "● Up to date" : `⚠ ${trust.freshness === "STALE" ? "Not recently refreshed" : trust.freshness || "Unknown"}`}</span><InfoTip k="freshness" /></span>
      <span className="flex items-center gap-1 text-slate-400">Data available: <span className={covTone} data-testid="in-coverage">{COV_TXT[coverage] || coverage || "—"}</span><InfoTip k="coverage" /></span>
      {profitStatus && <span className="text-slate-400">Operating result: <span className={profitStatus === "COMPLETE" ? "text-emerald-300" : "text-amber-300"}>{profitStatus === "COMPLETE" ? "Complete" : "Incomplete (running costs missing)"}</span></span>}
      {reconciliation && <span className="flex items-center gap-1 text-slate-400">Totals check: <span data-testid="in-reconciliation" className={reconciliation === "MATCHED" ? "text-emerald-300" : reconciliation === "SCOPED" ? "text-slate-400" : "text-rose-300"}>{REC_TXT[reconciliation] || reconciliation}</span><InfoTip k="reconciliation" /></span>}
      <span className="text-slate-500">Last refreshed: {trust.last_computed_at ? new Date(trust.last_computed_at).toLocaleString() : "—"}</span>
      <span className="text-slate-500">Calculation version {trust.computation_version || "—"}</span>
    </div>
  );
}
/* Explore/Export handoff — carries the current intelligence filters into the existing tools */
function ExploreExport({ f, exploreDataset }) {
  const navigate = useNavigate();
  const q = new URLSearchParams();
  if (f?.tenant) q.set("tenant_id", f.tenant);
  if (exploreDataset) q.set("dataset", exploreDataset);
  const suffix = q.toString() ? `?${q.toString()}` : "";
  return (
    <div className="flex gap-2">
      <button data-testid="in-explore" onClick={() => navigate(`/platform-admin/explorer${suffix}`)} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 px-3 py-2 text-sm text-slate-200 hover:bg-slate-800"><Database className="h-4 w-4" /> Explore data</button>
      <button data-testid="in-export" onClick={() => navigate(`/platform-admin/data-export${suffix}`)} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 px-3 py-2 text-sm text-slate-200 hover:bg-slate-800"><Download className="h-4 w-4" /> Export</button>
    </div>
  );
}
/* breadcrumbs built from the active drill-down filters */
function useVerticalCrumbs(f, title) {
  const base = `/platform-admin/intelligence`;
  const crumbs = [{ label: "Intelligence", to: base }, { label: title, to: `${base}/${title.toLowerCase()}${f.carry({ month: "", state: "", city: "", branch_id: "", tenant_id: "" })}` }];
  if (f.month) crumbs.push({ label: f.month, to: `${base}/${title.toLowerCase()}${f.carry({ state: "", city: "", branch_id: "", tenant_id: "" })}` });
  if (f.state) crumbs.push({ label: f.state });
  if (f.city) crumbs.push({ label: f.city });
  return crumbs;
}

function VerticalDash({ vname, path, title, subtitle, cards, trends, testid }) {
  const f = useFilters();
  const { data, loading, error, reload } = useApi(`/platform/intel/dash/${path}?${f.vqs}`, [f.vqs]);
  const cmp = useComparison(f.comparison !== "none", {
    vertical: vname || path, metrics: cards.map((c) => c.k), month: f.month, comparison: f.comparison,
    tenant_id: f.tenant, branch_id: f.branch, state: f.state, city: f.city,
    business_type: f.bt, plan: f.plan });
  return (
    <div data-testid={testid}>
      <Header title={`${title} Intelligence`} subtitle={subtitle} right={<div className="flex flex-col items-end gap-2">{f.node}<ExploreExport f={f} /></div>} />
      <Breadcrumbs items={useVerticalCrumbs(f, title)} />
      {data && <DataTrust trust={data.data_trust} coverage={data.coverage} />}
      {error ? <div className="mt-4"><ErrorState error={error} onRetry={reload} /></div>
        : loading || !data ? <Loading />
        : !data.data_available ? <div className="mt-4"><Empty label="No data" hint={`No ${title.toLowerCase()} records match the current filters.`} /></div>
        : (<>
          <div className="mt-5 grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {cards.map((c) => (
              <div key={c.k} className="rounded-xl border border-slate-800 bg-slate-900/50 p-4" data-testid={`${testid}-${c.k}`}>
                <div className="text-xs text-slate-400">{c.label}</div>
                <div className={`mt-1 font-display text-2xl font-bold ${c.tone || ""}`}>{fmtNum(data.totals[c.k])}</div>
                {cmp && <Delta cmp={cmp.byMetric[c.k]} />}
              </div>
            ))}
          </div>
          <div className="mt-5 grid gap-5 lg:grid-cols-2">
            {trends.map((t) => (
              <Card key={t.k} title={t.label}><Spark series={(data.monthly_trend || []).map((m) => ({ month: m.month, value: m[t.k] }))} money={t.money} /></Card>
            ))}
          </div>
          <HistoricalTrends catalog={TREND_CATALOG[vname || path] || TREND_CATALOG.overview} vertical={vname || path} testid="in-trends-vertical" />
        </>)}
    </div>
  );
}

export function IntelVeterinary() {
  return <VerticalDash testid="in-veterinary" path="veterinary" title="Veterinary" subtitle="Recorded clinical activity across Pawkt vet businesses."
    cards={[{ k: "encounters", label: "Encounters" }, { k: "completed_encounters", label: "Completed" }, { k: "emergency_encounters", label: "Emergency", tone: "text-rose-300" }, { k: "recorded_diagnoses", label: "Recorded conditions" }, { k: "vaccinations", label: "Vaccinations" }, { k: "unique_pets", label: "Unique pets" }]}
    trends={[{ k: "encounters", label: "Monthly encounters" }, { k: "recorded_diagnoses", label: "Recorded clinical conditions (Pawkt records)" }]} />;
}
export function IntelBoarding() {
  return <VerticalDash testid="in-boarding" path="boarding" title="Boarding" subtitle="Reservations, check-ins and stays."
    cards={[{ k: "reservations", label: "Reservations" }, { k: "check_ins", label: "Check-ins" }, { k: "check_outs", label: "Check-outs" }, { k: "cancellations", label: "Cancellations", tone: "text-amber-300" }, { k: "unique_pets", label: "Unique pets" }]}
    trends={[{ k: "reservations", label: "Monthly reservations" }, { k: "check_outs", label: "Monthly check-outs" }]} />;
}
export function IntelTraining() {
  return <VerticalDash testid="in-training" path="training" title="Training" subtitle="Packages, sessions and completion."
    cards={[{ k: "packages", label: "Packages" }, { k: "sessions", label: "Sessions" }, { k: "completed_sessions", label: "Completed" }, { k: "no_show_sessions", label: "No-shows", tone: "text-amber-300" }, { k: "unique_pets", label: "Unique pets" }]}
    trends={[{ k: "sessions", label: "Monthly sessions" }, { k: "completed_sessions", label: "Monthly completed" }]} />;
}

export function IntelFinance() {
  const f = useFilters();
  const { data, loading, error, reload } = useApi(`/platform/intel/dash/finance?${f.vqs}`, [f.vqs]);
  const cmp = useComparison(f.comparison !== "none", {
    vertical: "finance", metrics: ["net_revenue", "revenue", "refunds", "contribution_margin", "operating_result"],
    month: f.month, comparison: f.comparison, tenant_id: f.tenant, branch_id: f.branch,
    state: f.state, city: f.city, business_type: f.bt, plan: f.plan });
  const D = (m) => cmp ? <Delta cmp={cmp.byMetric[m]} money /> : null;
  return (
    <div data-testid="in-finance">
      <Header title="Finance Intelligence" subtitle="Sales, amount left after direct costs and operating result, from the original records." right={<div className="flex flex-col items-end gap-2">{f.node}<ExploreExport f={f} /></div>} />
      <Breadcrumbs items={useVerticalCrumbs(f, "Finance")} />
      {data && <DataTrust trust={data.data_trust} coverage={data.coverage} profitStatus={data.profit_status} />}
      {error ? <div className="mt-4"><ErrorState error={error} onRetry={reload} /></div>
        : loading || !data ? <Loading />
        : !data.data_available ? <div className="mt-4"><Empty label="No data" hint="No finance records match the current filters." /></div>
        : (<>
          <div className="mt-5 grid gap-3 sm:grid-cols-3 lg:grid-cols-4">
            <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-4" data-testid="in-finance-net"><div className="flex items-center gap-1 text-xs text-slate-400">Sales excl. GST, after refunds<InfoTip k="net_revenue" /></div><div className="mt-1 font-display text-2xl font-bold">{fmtINR(data.totals.net_revenue)}</div>{D("net_revenue")}</div>
            <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-4"><div className="flex items-center gap-1 text-xs text-slate-400">Sales excl. GST<InfoTip k="revenue" /></div><div className="mt-1 font-display text-2xl font-bold">{fmtINR(data.totals.revenue)}</div>{D("revenue")}</div>
            <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-4"><div className="text-xs text-slate-400">Refunds</div><div className="mt-1 font-display text-2xl font-bold text-amber-300">{fmtINR(data.totals.refunds)}</div>{D("refunds")}</div>
            <Stat label="Product and service cost" help="cogs" value={fmtINR(data.totals.cogs)} />
            <Stat label="Staff commissions" help="commissions" value={fmtINR(data.totals.commissions)} />
            <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-4" data-testid="in-finance-cm"><div className="flex items-center gap-1 text-xs text-slate-400">Amount left after direct costs<InfoTip k="margin" /></div><div className="mt-1 font-display text-2xl font-bold text-indigo-300">{fmtINR(data.totals.contribution_margin)}</div><div className="text-xs text-slate-500">Not profit — running costs not deducted</div>{D("contribution_margin")}</div>
            <Stat label="Running costs" help="opex" value={fmtINR(data.totals.opex_total)} />
            <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-4" data-testid="in-finance-op"><div className="flex items-center gap-1 text-xs text-slate-400">Operating result<InfoTip k="operating_result" /></div><div className={`mt-1 font-display text-2xl font-bold ${data.profit_status === "COMPLETE" ? "text-emerald-300" : "text-amber-300"}`}>{fmtINR(data.totals.operating_result)}</div>{D("operating_result")}</div>
          </div>
          {data.profit_status !== "COMPLETE" && (
            <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-4 py-2 text-xs text-amber-200" data-testid="in-finance-profit-warning">
              <AlertTriangle className="h-4 w-4 shrink-0" /><span>{data.profit_note}</span>
            </div>
          )}
          <div className="mt-2 text-xs text-slate-500">{data.disclaimer}</div>
          <div className="mt-5 grid gap-5 lg:grid-cols-2">
            <Card title="Monthly sales excl. GST, after refunds"><Spark series={(data.monthly_trend || []).map((m) => ({ month: m.month, value: m.net_revenue }))} money /></Card>
            <Card title="Monthly amount left after direct costs"><Spark series={(data.monthly_trend || []).map((m) => ({ month: m.month, value: m.contribution_margin }))} money /></Card>
          </div>
          <HistoricalTrends catalog={TREND_CATALOG.finance} vertical="finance" testid="in-trends-finance" />
        </>)}
    </div>
  );
}

/* ------------------------------------------------------------ 6G-3 business detail */
export function IntelBusinessDetail() {
  const { tenantId } = useParams();
  const f = useFilters();
  const path = `/platform/intel/dash/business/${tenantId}?month=${enc(f.month)}&branch_id=${enc(f.branch)}`;
  const { data, loading, error, reload } = useApi(path, [path]);
  const navigate = useNavigate();
  const bcmp = useComparison(f.comparison !== "none", {
    vertical: "finance", metrics: ["revenue", "net_revenue", "contribution_margin", "operating_result"],
    month: f.month, comparison: f.comparison, tenant_id: tenantId, branch_id: f.branch });
  const p = data?.profile;
  const crumbs = [{ label: "Intelligence", to: "/platform-admin/intelligence" }];
  if (f.state) crumbs.push({ label: f.state, to: `/platform-admin/intelligence/geography${f.carry({ city: "", tenant_id: "" })}` });
  if (f.city) crumbs.push({ label: f.city, to: `/platform-admin/intelligence/geography${f.carry({ tenant_id: "" })}` });
  crumbs.push({ label: p?.name || "Business" });
  return (
    <div data-testid="in-business-detail">
      <Header title={p?.name || "Business Intelligence"} subtitle="View-only business summary — no customer names, phone numbers or addresses." right={<div className="flex flex-col items-end gap-2">{f.node}<div className="flex gap-2"><button data-testid="in-bd-compare" onClick={() => navigate(`/platform-admin/intelligence/compare?metric=revenue&tenant_id=${tenantId}${f.month ? `&period=${f.month}` : ""}`)} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 px-3 py-2 text-sm text-slate-200 hover:bg-slate-800"><Users className="h-4 w-4" /> Compare with similar businesses</button><ExploreExport f={{ tenant: tenantId }} exploreDataset="businesses" /></div></div>} />
      <Breadcrumbs items={crumbs} />
      {data && <DataTrust trust={data.data_trust} coverage={data.coverage} profitStatus={data.profit_status} reconciliation={data.reconciliation} />}
      {error ? <div className="mt-4"><ErrorState error={error} onRetry={reload} /></div>
        : loading || !data ? <Loading />
        : !data.data_available ? <div className="mt-4"><Empty label="Business not found" /></div>
        : (<>
          <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-slate-400" data-testid="in-bd-identity">
            <span className="rounded-md bg-slate-800 px-2 py-1 text-slate-200">{p.plan}</span>
            <span>{(p.business_types || []).join(", ") || "—"}</span>
            <span>· {p.city || "—"}</span>
            <span>· {p.branch_count} branches</span>
            <span className={p.status === "suspended" ? "text-rose-400" : "text-emerald-400"}>· {p.status}</span>
          </div>
          <h3 className="mt-5 font-semibold text-slate-200">Activity</h3>
          <div className="mt-2 grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <Stat testid="in-bd-customers" label="Customers" value={fmtNum(data.activity.customers)} />
            <Stat label="Pets" value={fmtNum(data.activity.pets)} />
            <Stat label="Bookings" value={fmtNum(data.activity.bookings)} />
            <Stat label="Completed" value={fmtNum(data.activity.completed_bookings)} />
            <Stat label="Cancellations" value={fmtNum(data.activity.cancellations)} tone="text-amber-300" />
            <Stat label="No-shows" value={fmtNum(data.activity.no_shows)} tone="text-amber-300" />
          </div>
          <UnusualMovements tenant_id={tenantId} testid="in-bd-unusual" />
          <h3 className="mt-5 font-semibold text-slate-200">Financial</h3>
          <div className="mt-2 grid gap-3 sm:grid-cols-3 lg:grid-cols-4">
            <Stat testid="in-bd-revenue" label="Sales excl. GST" help="revenue" value={fmtINR(data.financial.revenue)} sub={bcmp ? cmpSub(bcmp.byMetric.revenue) : undefined} />
            <Stat label="Money received (before refunds)" help="money_received" value={fmtINR(data.financial.gmv)} />
            <Stat label="Refunds" value={fmtINR(data.financial.refunds)} tone="text-amber-300" />
            <Stat label="Sales excl. GST, after refunds" help="net_revenue" value={fmtINR(data.financial.net_revenue)} sub={bcmp ? cmpSub(bcmp.byMetric.net_revenue) : undefined} />
            <Stat label="Product and service cost" help="cogs" value={fmtINR(data.financial.cogs)} />
            <Stat label="Staff commissions" help="commissions" value={fmtINR(data.financial.commissions)} />
            <Stat testid="in-bd-cm" label="Amount left after direct costs" help="margin" value={fmtINR(data.financial.contribution_margin)} sub={bcmp && cmpSub(bcmp.byMetric.contribution_margin) ? cmpSub(bcmp.byMetric.contribution_margin) : "Not profit"} tone="text-indigo-300" />
            <Stat label="Running costs" help="opex" value={fmtINR(data.financial.opex_total)} />
            <Stat testid="in-bd-op" label="Operating result" help="operating_result" value={fmtINR(data.financial.operating_result)} tone={data.profit_status === "COMPLETE" ? "text-emerald-300" : "text-amber-300"} />
          </div>
          <div className="mt-2 flex flex-wrap gap-4" data-testid="in-bd-evidence-row">
            <EvidenceButton metric="revenue" label="Sales excl. GST" tenant_id={tenantId} month={f.month} testid="in-bd-ev-revenue" />
            <EvidenceButton metric="contribution_margin" label="Amount left after direct costs" vertical="finance" tenant_id={tenantId} month={f.month} testid="in-bd-ev-cm" />
            <EvidenceButton metric="bookings_created" label="Bookings" tenant_id={tenantId} month={f.month} testid="in-bd-ev-bookings" />
          </div>
          {data.profit_status !== "COMPLETE" && <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-4 py-2 text-xs text-amber-200" data-testid="in-bd-profit-warning"><AlertTriangle className="h-4 w-4 shrink-0" /><span>{data.profit_note}</span></div>}
          <div className="mt-5 grid gap-5 lg:grid-cols-2">
            <Card title="Money received by service type" testid="in-bd-mix"><RevenueDonut mix={data.vertical_mix.mix} /></Card>
            <div className="space-y-5">
              <Card title="Customers">
                <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
                  <div><div className="text-slate-500">Active</div><div className="font-semibold text-emerald-300">{fmtNum(data.customers.active)}</div></div>
                  <div><div className="text-slate-500">Inactive (no recent visit)</div><div className="font-semibold text-amber-300">{fmtNum(data.customers.dormant)}</div></div>
                  <div><div className="text-slate-500">New</div><div className="font-semibold">{fmtNum(data.customers.new)}</div></div>
                  <div className="col-span-2 sm:col-span-3"><div className="text-slate-500">Lifetime spend</div><div className="font-semibold">{fmtINR(data.customers.lifetime_spend)}</div></div>
                </div>
              </Card>
              <Card title="Inventory">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><div className="text-slate-500">Stock value</div><div className="font-semibold">{fmtINR(data.inventory.stock_value)}</div></div>
                  <div><div className="text-slate-500">Units used</div><div className="font-semibold">{fmtNum(data.inventory.consumption)}</div></div>
                  <div><div className="text-slate-500">Low stock</div><div className="font-semibold text-amber-300">{fmtNum(data.inventory.low_stock)}</div></div>
                  <div><div className="text-slate-500">Not selling (no recent movement)</div><div className="font-semibold">{fmtNum(data.inventory.dead_stock)}</div></div>
                </div>
              </Card>
            </div>
          </div>
          <h3 className="mt-5 font-semibold text-slate-200">Branches</h3>
          <div className="mt-2 overflow-x-auto rounded-xl border border-slate-800">
            <table className="w-full text-sm"><thead className="bg-slate-900/70 text-left text-xs uppercase text-slate-500"><tr>{["Branch", "City", ""].map((h) => <th key={h} className="px-3 py-2.5 font-medium">{h}</th>)}</tr></thead>
              <tbody>{data.branches.map((b) => (
                <tr key={b.branch_id} data-testid={`in-bd-branch-${b.branch_id}`} onClick={() => navigate(`/platform-admin/intelligence/business/${tenantId}/branch/${b.branch_id}${f.carry()}`)} className="cursor-pointer border-t border-slate-800 hover:bg-slate-800/40">
                  <td className="px-3 py-2.5 font-medium text-slate-100">{b.name}{b.is_primary && <span className="ml-2 text-xs text-slate-500">primary</span>}</td>
                  <td className="px-3 py-2.5">{b.city || "—"}</td>
                  <td className="px-3 py-2.5 text-right text-indigo-300"><ChevronRight className="inline h-4 w-4" /></td>
                </tr>))}
                {!data.branches.length && <tr><td colSpan={3} className="px-3 py-6 text-center text-slate-500">No branches</td></tr>}
              </tbody></table>
          </div>
          <HistoricalTrends catalog={TREND_CATALOG.business} tenant_id={tenantId} testid="in-trends-business" />
          <div className="mt-3 text-xs text-slate-500">{data.disclaimer}</div>
        </>)}
    </div>
  );
}

/* ------------------------------------------------------------ 6G-3 branch detail */
export function IntelBranchDetail() {
  const { tenantId, branchId } = useParams();
  const f = useFilters();
  const path = `/platform/intel/dash/business/${tenantId}/branch/${branchId}?month=${enc(f.month)}`;
  const { data, loading, error, reload } = useApi(path, [path]);
  const brcmp = useComparison(f.comparison !== "none", {
    vertical: "finance", metrics: ["revenue", "contribution_margin"], month: f.month,
    comparison: f.comparison, tenant_id: tenantId, branch_id: branchId });
  const p = data?.profile;
  const crumbs = [{ label: "Intelligence", to: "/platform-admin/intelligence" },
    { label: p?.business_name || "Business", to: `/platform-admin/intelligence/business/${tenantId}${f.carry()}` },
    { label: p?.name || "Branch" }];
  const brSub = (m) => brcmp ? cmpSub(brcmp.byMetric[m]) : undefined;
  return (
    <div data-testid="in-branch-detail">
      <Header title={p?.name || "Branch Intelligence"} subtitle="View-only branch summary — no customer names, phone numbers or addresses." right={<div className="flex flex-col items-end gap-2">{f.node}<ExploreExport f={{ tenant: tenantId }} exploreDataset="branches" /></div>} />
      <Breadcrumbs items={crumbs} />
      {data && <DataTrust trust={data.data_trust} coverage={data.coverage} profitStatus={data.profit_status} />}
      {error ? <div className="mt-4"><ErrorState error={error} onRetry={reload} /></div>
        : loading || !data ? <Loading />
        : !data.data_available ? <div className="mt-4"><Empty label="Branch not found" /></div>
        : (<>
          <div className="mt-4 text-sm text-slate-400" data-testid="in-brd-identity">{p.business_name} · {p.city || "—"}</div>
          <div className="mt-4 grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <Stat testid="in-brd-customers" label="Customers" value={fmtNum(data.activity.customers)} />
            <Stat label="Pets" value={fmtNum(data.activity.pets)} />
            <Stat label="Bookings" value={fmtNum(data.activity.bookings)} />
            <Stat testid="in-brd-revenue" label="Sales excl. GST" help="revenue" value={fmtINR(data.financial.revenue)} sub={brSub("revenue")} />
            <Stat label="Money received (before refunds)" help="money_received" value={fmtINR(data.financial.gmv)} />
            <Stat label="Refunds" value={fmtINR(data.financial.refunds)} tone="text-amber-300" />
            <Stat testid="in-brd-cm" label="Amount left after direct costs" help="margin" value={fmtINR(data.financial.contribution_margin)} sub="Not profit" tone="text-indigo-300" />
            <Stat label="Running costs" help="opex" value={fmtINR(data.financial.opex_total)} />
            <Stat testid="in-brd-op" label="Operating result" help="operating_result" value={fmtINR(data.financial.operating_result)} tone={data.profit_status === "COMPLETE" ? "text-emerald-300" : "text-amber-300"} />
          </div>
          {data.profit_status !== "COMPLETE" && <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-4 py-2 text-xs text-amber-200"><AlertTriangle className="h-4 w-4 shrink-0" /><span>Operating result is incomplete — running costs (rent, salaries, utilities) are not fully recorded.</span></div>}
          <div className="mt-2 flex flex-wrap gap-4" data-testid="in-brd-evidence-row">
            <EvidenceButton metric="revenue" label="Sales excl. GST" tenant_id={tenantId} branch_id={branchId} month={f.month} testid="in-brd-ev-revenue" />
            <EvidenceButton metric="contribution_margin" label="Amount left after direct costs" vertical="finance" tenant_id={tenantId} branch_id={branchId} month={f.month} testid="in-brd-ev-cm" />
          </div>
          <UnusualMovements tenant_id={tenantId} branch_id={branchId} testid="in-brd-unusual" />
          <Card title="Service mix (by number of bookings)" testid="in-brd-mix" >
            <BarList items={data.service_mix.mix.filter((m) => m.bookings > 0)} labelKey="label" valueKey="bookings" />
          </Card>
          <HistoricalTrends catalog={TREND_CATALOG.business} tenant_id={tenantId} branch_id={branchId} testid="in-trends-branch" />
          <div className="mt-3 text-xs text-slate-500">{data.disclaimer}</div>
        </>)}
    </div>
  );
}
